import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";
import { PROPERTY_TYPES, UNIT_STATUS, METER_TYPES, METER_UNIT_SUGGESTION, INSURANCE_TYPES, HEATING_TYPES, HEATING_ENERGIES, HOT_WATER_TYPES, ENERGY_CERT_TYPES, ROLE_LABELS, TENANCY_STATUS, UNIT_TYPES, formatDate, firstEmail, displayEmail, FUEL_TANKS, tankFuelOf } from "@/lib/constants";
import { UNIT_KINDS, FLOOR_CODES, POSITIONS, UNIT_SIDES, APARTMENT_SUBTYPES, buildUnitNumber, FURNISHING_OPTIONS, QUALITY_STANDARD_OPTIONS, GARAGE_KIND_OPTIONS, GARAGE_DOOR_OPTIONS } from "@/lib/domivus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, ArrowLeft, ArrowRight, Trash, PencilSimple, FloppyDisk, X,
  HouseLine, Buildings, Thermometer, ShieldCheck, UserGear, Gauge,
  FileText, Key, Wrench, UploadSimple, DownloadSimple, PaperPlaneTilt,
  Phone, EnvelopeSimple, Storefront, Lightning, Clock, Eye, Camera, Warning, ArrowsLeftRight, ChatsCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import MessagingDialog from "@/components/MessagingDialog";
import { NewListingDialog } from "@/components/marketing/ListingEditor";
import ChangeHistoryDialog from "@/components/ChangeHistoryDialog";
import DocumentPreviewDrawer from "@/components/DocumentPreviewDrawer";
import { useConfirm } from "@/components/ConfirmDialog";
import WEGPropertyTab from "@/components/property/WEGPropertyTab";
import PropertyDocumentFolder from "@/components/property/PropertyDocumentFolder";
import PriorManagerPanel from "@/components/property/PriorManagerPanel";
import OwnerMoveDialog from "@/components/property/OwnerMoveDialog";
import CommunicationTab from "@/components/communication/CommunicationTab";
import FolderUploads from "@/components/communication/FolderUploads";
import OwnerHistoryList from "@/components/property/OwnerHistoryList";
import ProtocolsPanel from "@/components/ProtocolsPanel";
import OpeningBalancesWizard from "@/components/OpeningBalancesWizard";
import SearchableCombobox from "@/components/SearchableCombobox";
import { CommercialTypeSelect, CommercialFields, commercialFormFrom, commercialPayload, deriveUnitType, unitLabels } from "@/components/units/CommercialUnitFields";
import { UnitLinkBadges, unitTypeText } from "@/components/units/UnitLinks";
import { TankManager } from "@/components/bka/OilHeatingDialog";
import { DatePicker } from "@/components/ui/date-picker";
import { CalibrationField, UnitMultiSelect, MeterChangeDialog, calibrationInfo } from "@/components/meters/MeterExtras";


const TABS = [
  { key: "units",       label: "Einheiten",     icon: HouseLine },
  { key: "master",      label: "Stammdaten",    icon: Buildings },
  { key: "weg",         label: "WEG",           icon: Buildings, wegOnly: true },
  { key: "seg_analyses", label: "SEG-Analysen", icon: Buildings, segOnly: true },
  { key: "heating",     label: "Heizung",       icon: Thermometer },
  { key: "energy",      label: "Energie",       icon: Lightning },
  { key: "insurances",  label: "Versich.",      icon: ShieldCheck },
  { key: "caretaker",   label: "Betreuer",      icon: UserGear },
  { key: "caretakers",  label: "Hausmeister",   icon: Wrench },
  { key: "keys",        label: "Schlüssel",     icon: Key },
  { key: "meters",      label: "Zähler",        icon: Gauge },
  { key: "damages",     label: "Schäden",       icon: Warning },
  { key: "documents",   label: "Dokumente",     icon: FileText },
  { key: "doc_folder",  label: "Dokumentenmappe", icon: FileText },
  { key: "communication", label: "Kommunikation", icon: ChatsCircle },
];

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  // Deep-Link ?tab=weg unterstützt (z. B. aus WEG-Übersicht)
  const initialTab = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tab")) || "units";
  const [tab, setTab] = useState(initialTab);
  const [openHistory, setOpenHistory] = useState(false);
  const [listingOpen, setListingOpen] = useState(false);
  const [mkMeta, setMkMeta] = useState(null);
  const [openDeleteProperty, setOpenDeleteProperty] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [linkedTenancies, setLinkedTenancies] = useState([]);
  const [tenanciesToDelete, setTenanciesToDelete] = useState({}); // id -> bool
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [linkedLoadError, setLinkedLoadError] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openOpeningBalances, setOpenOpeningBalances] = useState(false);
  const [openingBalancesAutoAsked, setOpeningBalancesAutoAsked] = useState(false);

  // Auto-Trigger: Wenn Property management_start hat, aber noch nie Übernahme
  // erfasst wurde → Dialog vorschlagen (nur einmal pro Session)
  useEffect(() => {
    if (!p || openingBalancesAutoAsked) return;
    if (p.management_start && !p.opening_balances_captured_at) {
      setOpeningBalancesAutoAsked(true);
      // Kleine Verzögerung — sonst überrascht der Dialog beim Laden
      const t = setTimeout(() => setOpenOpeningBalances(true), 800);
      return () => clearTimeout(t);
    }
  }, [p, openingBalancesAutoAsked]);

  // Beim Öffnen des Lösch-Dialogs: verknüpfte Mietverhältnisse laden
  useEffect(() => {
    if (!openDeleteProperty || !p?.id) return;
    setLoadingLinked(true);
    setLinkedLoadError(false);
    api.get(`/properties/${p.id}/linked-tenancies`)
      .then(({ data }) => {
        setLinkedTenancies(data || []);
        // Default: aktive MV bekommen Häkchen, archivierte/beendete nicht
        const def = {};
        (data || []).forEach((t) => { def[t.id] = t.status === "active"; });
        setTenanciesToDelete(def);
      })
      .catch((e) => {
        setLinkedTenancies([]);
        setLinkedLoadError(true);
        toast.error("Verknüpfte Mietverhältnisse konnten nicht geladen werden: " + formatApiError(e));
      })
      .finally(() => setLoadingLinked(false));
  }, [openDeleteProperty, p?.id]);

  const handleDeleteProperty = async () => {
    setDeleting(true);
    try {
      // Erst die ausgewählten Mietverhältnisse löschen, dann das Objekt
      const idsToDelete = Object.entries(tenanciesToDelete)
        .filter(([, v]) => v).map(([k]) => k);
      for (const tid of idsToDelete) {
        try {
          await api.delete(`/tenancies/${tid}`);
        } catch (e) {
          // Einzelne Fehler nicht den Gesamtprozess blockieren — Toast
          toast.error(`Mietverhältnis ${tid.slice(0, 8)}…: ${formatApiError(e)}`);
        }
      }
      await api.delete(`/properties/${p.id}`);
      const archivedCount = idsToDelete.length;
      toast.success(
        archivedCount > 0
          ? `Objekt gelöscht. ${archivedCount} verknüpftes Mietverhältnis archiviert.`
          : "Objekt gelöscht."
      );
      navigate("/objekte");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const load = async () => {
    const { data } = await api.get(`/properties/${id}`);
    setP(data);
  };
  useEffect(() => { load(); }, [id]);

  if (!p) return <div className="text-sm text-slate-500">Lade Objekt...</div>;

  return (
    <div className="space-y-4" data-testid="property-detail">
      <button
        type="button"
        onClick={() => {
          if (window.history.length > 1) navigate(-1); else navigate("/objekte");
        }}
        className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        data-testid="property-back-btn"
      >
        <ArrowLeft size={14} /> Zurück
      </button>

      <div className="bg-white/90 backdrop-blur border border-stone-200 rounded-md p-5 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold font-mono">{p.property_number}</div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 mt-0.5">{p.name}</h1>
            <p className="text-sm text-slate-600 mt-1">
              {p.address?.street} {p.address?.house_number}, {p.address?.zip} {p.address?.city}
            </p>
            {/* 2026-02 (iter117) — Objekt-Typ und WEG-Verwaltung sind fundamentale
                Stammdaten und werden jetzt nur noch in Stammdaten > Bearbeiten
                geändert. Hier nur noch Read-Only-Badges zur Orientierung. */}
            <div className="mt-2 inline-flex items-center gap-1.5 flex-wrap text-[11px]" data-testid="property-type-badges">
              {(() => {
                const otype = p.object_type || "rental";
                const OTLABEL = { rental: "Miethaus", weg: "WEG", seg: "SEG (Sondereigentum)", mixed: "Mixed (Miete + WEG)" };
                const mgmt = p.weg_management
                  || (otype === "weg" || otype === "mixed" ? "enabled"
                     : otype === "seg" ? "external" : "none");
                const MGLABEL = { enabled: "Wir verwalten die WEG", external: "Fremd-verwaltete WEG", none: "Keine WEG" };
                return (
                  <>
                    <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-900 font-semibold" data-testid="prop-badge-otype">
                      {OTLABEL[otype]}
                    </span>
                    {(otype === "weg" || otype === "mixed" || otype === "seg") && (
                      <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700" data-testid="prop-badge-mgmt">
                        WEG-Verwaltung: {MGLABEL[mgmt]}
                      </span>
                    )}
                    <span className="text-slate-400 text-[10px] italic">
                      Änderungen in Stammdaten &gt; Bearbeiten
                    </span>
                  </>
                );
              })()}
            </div>
          </div>
          <div className="text-right text-xs text-slate-500 space-y-0.5">
            <div className="font-medium text-slate-700">{PROPERTY_TYPES[p.property_type]}</div>
            <div className="flex items-center gap-1.5 justify-end mt-3">
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs text-emerald-800 hover:text-emerald-900 hover:bg-emerald-50 border-emerald-200"
                onClick={() => setOpenOpeningBalances(true)}
                data-testid="property-open-opening-balances-btn"
              >
                <Clock size={13} /> Übernahmesalden erfassen
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                onClick={() => window.dispatchEvent(new CustomEvent("domivus:open-composer", { detail: { context: { property_id: p.id } } }))}
                data-testid="property-write-owner-btn"
              >
                <EnvelopeSimple size={13} /> An Eigentümer schreiben
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                onClick={() => { setListingOpen(true); if (!mkMeta) api.get("/marketing/meta").then(({ data }) => setMkMeta(data)).catch(() => {}); }}
                data-testid="property-create-listing-btn"
              >
                <Storefront size={13} /> Inserat schalten
              </Button>
              <NewListingDialog open={listingOpen} onOpenChange={setListingOpen} meta={mkMeta} presetPropertyId={p.id}
                onCreated={(l) => navigate(`/vermarktung/inserat/${l.id}?tab=edit`)} />
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                onClick={() => setOpenHistory(true)}
                data-testid="property-open-history-btn"
              >
                <Clock size={13} /> Änderungsverlauf
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                onClick={async () => {
                  try {
                    const r = await api.get(`/rental-overview/export`, { params: { property_id: p.id, format: 'pdf' }, responseType: 'blob' });
                    const url = URL.createObjectURL(r.data);
                    window.open(url, '_blank');
                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                  } catch (e) { toast.error("Export fehlgeschlagen"); }
                }}
                data-testid="rental-overview-pdf-btn"
              >
                <FileText size={13} /> Vermietungsübersicht (PDF)
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                onClick={async () => {
                  try {
                    const r = await api.get(`/rental-overview/export`, { params: { property_id: p.id, format: 'xlsx' }, responseType: 'blob' });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement('a');
                    a.href = url; a.download = `vermietungsuebersicht-${p.property_number || 'objekt'}.xlsx`; a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                  } catch (e) { toast.error("Export fehlgeschlagen"); }
                }}
                data-testid="rental-overview-xlsx-btn"
              >
                <DownloadSimple size={13} /> Excel
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs text-rose-700 hover:text-rose-800 hover:bg-rose-50 border-rose-200"
                onClick={() => setOpenDeleteProperty(true)}
                data-testid="property-delete-btn"
              >
                <Trash size={13} /> Objekt löschen
              </Button>
            </div>
          </div>
        </div>

        {/* Horizontale Tab-Leiste */}
        <nav
          className="mt-5 -mx-5 -mb-5 px-2 pt-2 border-t border-stone-200 bg-stone-50/70 rounded-b-md flex flex-nowrap gap-0.5 overflow-x-auto scrollbar-thin"
          data-testid="property-tabs"
        >
          {TABS.filter((t) =>
            (!t.wegOnly || (p?.object_type === "weg" || p?.object_type === "mixed"))
            && (!t.segOnly || p?.object_type === "seg")
          ).map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`text-xs lg:text-[13px] px-2 lg:px-2.5 py-1.5 rounded-t-md flex items-center gap-1.5 transition-colors whitespace-nowrap shrink-0 ${
                  active
                    ? "bg-white text-slate-900 border-x border-t border-stone-200 -mb-px shadow-[0_-1px_0_rgba(0,0,0,0.02)]"
                    : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
                }`}
                data-testid={`tab-${t.key}`}
              >
                <Icon size={13} weight={active ? "fill" : "regular"} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Inhalt */}
      <section>
        <ErrorBoundary key={tab}>
        {tab === "units"      && <UnitsTab property={p} onChange={load} />}
        {tab === "master"     && <MasterTab property={p} onChange={load} />}
        {tab === "weg"        && (p?.object_type === "weg" || p?.object_type === "mixed") && <WEGPropertyTab property={p} onChange={load} />}
        {tab === "seg_analyses" && p?.object_type === "seg" && <SegAnalysesTab property={p} />}
        {tab === "heating"    && <HeatingTab property={p} onChange={load} />}
        {tab === "energy"     && <EnergyTab property={p} onChange={load} />}
        {tab === "insurances" && <InsurancesTab property={p} onChange={load} />}
        {tab === "caretaker"  && <CaretakerTab property={p} onChange={load} />}
        {tab === "caretakers" && <CaretakersTab property={p} onChange={load} />}
        {tab === "keys"       && <KeysTab property={p} onChange={load} />}
        {tab === "meters"     && <MetersTab property={p} onChange={load} />}
        {tab === "damages"    && <PropertyDamagesTab property={p} />}
        {tab === "documents"  && <DocumentsTab property={p} />}
        {tab === "communication" && <CommunicationTab scope={{ property_id: p.id }} ownerTarget={{ property_id: p.id }} title={p.name} />}
        {tab === "doc_folder" && (
          <div className="space-y-6">
            <FolderUploads scope={{ property_id: p.id }} ownerTarget={{ property_id: p.id }} />
            <PriorManagerPanel property={p} onChange={load} />
            <PropertyDocumentFolder propertyId={p.id} />
          </div>
        )}
        </ErrorBoundary>
      </section>

      <ChangeHistoryDialog
        entityType="property"
        entityId={p.id}
        title={p.property_number}
        open={openHistory}
        onOpenChange={setOpenHistory}
      />
      <OpeningBalancesWizard
        open={openOpeningBalances}
        propertyId={p.id}
        onClose={() => setOpenOpeningBalances(false)}
        onDone={() => { setOpenOpeningBalances(false); load?.(); }}
      />
      {/* Sprint 5a — Property-Delete-Dialog mit Bestätigung */}
      <Dialog open={openDeleteProperty} onOpenChange={(o) => { setOpenDeleteProperty(o); if (!o) { setDeleteConfirmText(""); setLinkedTenancies([]); setTenanciesToDelete({}); setLinkedLoadError(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="property-delete-dialog">
          <DialogHeader>
            <DialogTitle>Objekt {`„${p.name}"`} wirklich löschen?</DialogTitle>
            <DialogDescription>
              Alle Einheiten, Mietverhältnisse und Dokumente bleiben in der Historie erhalten,
              werden aber als <span className="font-semibold">gelöscht</span> markiert und in der Liste ausgeblendet.
              Zur Bestätigung tippe bitte die Objektnummer <span className="font-mono font-semibold">{p.property_number}</span> ein.
            </DialogDescription>
          </DialogHeader>

          {/* Verknüpfte Mietverhältnisse – User entscheidet pro MV */}
          {loadingLinked ? (
            <div className="text-sm text-slate-500 py-3" data-testid="linked-tenancies-loading">
              Lade verknüpfte Mietverhältnisse…
            </div>
          ) : linkedLoadError ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="linked-tenancies-error">
              Verknüpfte Mietverhältnisse konnten nicht geladen werden. Bitte schließe diesen Dialog und versuche es erneut, bevor du das Objekt löschst.
            </div>
          ) : linkedTenancies.length > 0 ? (
            <div className="border border-amber-200 bg-amber-50/60 rounded-md p-3" data-testid="linked-tenancies-block">
              <div className="text-sm font-medium text-amber-900 mb-2">
                {linkedTenancies.length} verknüpfte{linkedTenancies.length === 1 ? "s" : ""} Mietverhältnis{linkedTenancies.length === 1 ? "" : "se"} gefunden.
              </div>
              <div className="text-[12px] text-amber-800 mb-2">
                Bitte entscheide für jedes Mietverhältnis einzeln, ob es ebenfalls gelöscht (archiviert) werden soll.
                Nicht gewählte bleiben bestehen.
              </div>
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {linkedTenancies.map((t) => (
                  <label
                    key={t.id}
                    className="flex items-start gap-2 text-sm cursor-pointer bg-white border border-amber-200 rounded px-2.5 py-2"
                    data-testid={`linked-tenancy-row-${t.id}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 accent-rose-600"
                      checked={!!tenanciesToDelete[t.id]}
                      onChange={(e) => setTenanciesToDelete((s) => ({ ...s, [t.id]: e.target.checked }))}
                      data-testid={`linked-tenancy-checkbox-${t.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-slate-700">{t.contract_number || "—"}</span>
                        <span className="text-[11px] text-slate-500">·</span>
                        <span className="text-xs text-slate-600">Einheit {t.unit_number || "—"}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${TENANCY_STATUS[t.status]?.className || "bg-slate-100 text-slate-700"}`}>
                          {TENANCY_STATUS[t.status]?.label || t.status}
                        </span>
                      </div>
                      <div className="text-xs text-slate-700 mt-0.5 truncate">
                        {t.tenants?.length > 0 ? t.tenants.join(", ") : <span className="text-slate-400">Keine Mieter</span>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic py-1" data-testid="linked-tenancies-none">
              Keine Mietverhältnisse mit diesem Objekt verknüpft.
            </div>
          )}

          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={p.property_number}
            className="font-mono"
            data-testid="property-delete-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDeleteProperty(false)} disabled={deleting}>Abbrechen</Button>
            <Button
              className="bg-rose-700 hover:bg-rose-800"
              onClick={handleDeleteProperty}
              disabled={deleteConfirmText !== p.property_number || deleting || loadingLinked || linkedLoadError}
              data-testid="property-delete-confirm-btn"
            >
              {deleting ? "Lösche…" : "Endgültig löschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Shared helpers
// ============================================================
const eur = (v) => (v == null || v === "" ? "—" : `${Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`);
const ja = (v) => (v == null ? "—" : v ? "Ja" : "Nein");

function SectionCard({ title, action, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-md shadow-sm">
      <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between bg-stone-50/40 rounded-t-md">
        <h2 className="text-sm font-semibold tracking-tight text-slate-900">{title}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function InfoTile({ label, value, hint }) {
  return (
    <div className="bg-stone-50 border border-stone-100 rounded-sm px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-800 truncate" title={String(value)}>{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{hint}</div>}
    </div>
  );
}

function F({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

async function patchProperty(id, patch) {
  await api.patch(`/properties/${id}`, patch);
}

// ============================================================
// Einheiten Tab
// ============================================================
function UnitsTab({ property: p, onChange }) {
  const [open, setOpen] = useState(false);
  const [editUnit, setEditUnit] = useState(null); // unit-obj zum Bearbeiten
  const [deleteUnit, setDeleteUnit] = useState(null); // unit-obj zum Löschen
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (!deleteUnit) return;
    try {
      await api.delete(`/properties/units/${deleteUnit.id}`);
      toast.success(`Einheit ${deleteUnit.unit_number} gelöscht`);
      setDeleteUnit(null);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen nicht möglich");
    }
  };

  return (
    <SectionCard
      title={`Einheiten (${(p.units || []).length})`}
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1.5 h-8" data-testid="add-unit-btn">
              <Plus size={14} /> Einheit hinzufügen
            </Button>
          </DialogTrigger>
          <UnitDialog key={open ? "open" : "closed"} propertyId={p.id} propertyNumber={p.property_number} objectType={p.object_type} property={p} onSaved={() => { setOpen(false); onChange(); }} />
        </Dialog>
      }
    >
      {/* Edit-Dialog */}
      {editUnit && (
        <Dialog open={!!editUnit} onOpenChange={(o) => !o && setEditUnit(null)}>
          <UnitDialog
            propertyId={p.id}
            propertyNumber={p.property_number}
            objectType={p.object_type}
            property={p}
            existing={editUnit}
            onSaved={() => { setEditUnit(null); onChange(); }}
          />
        </Dialog>
      )}
      {/* Delete-Confirm Dialog */}
      {deleteUnit && (
        <Dialog open={!!deleteUnit} onOpenChange={(o) => !o && setDeleteUnit(null)}>
          <DialogContent data-testid="unit-delete-dialog">
            <DialogHeader>
              <DialogTitle>Einheit löschen?</DialogTitle>
              <DialogDescription>
                Die Einheit <span className="font-mono font-semibold">{deleteUnit.unit_number}</span>
                {deleteUnit.location ? ` (${deleteUnit.location})` : ""} wird endgültig gelöscht.
                Falls die Einheit mit einem aktiven Mietverhältnis verknüpft ist, wird das Löschen verhindert.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteUnit(null)}>Abbrechen</Button>
              <Button className="bg-rose-600 hover:bg-rose-700" onClick={handleDelete} data-testid="unit-delete-confirm">
                Endgültig löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <div className="overflow-x-auto">
        {/* Mobile: Karten */}
        <div className="md:hidden divide-y divide-stone-100" data-testid="units-mobile-list">
          {(p.units || []).length === 0 && <div className="p-6 text-center text-slate-500 text-sm">Keine Einheiten vorhanden.</div>}
          {(p.units || []).map((u) => {
            const s = UNIT_STATUS[u.status] || { label: u.status, className: "bg-slate-100" };
            return (
              <div
                key={u.id}
                className="p-3 active:bg-stone-50"
                data-testid={`unit-card-${u.id}`}
              >
                <div
                  className="cursor-pointer"
                  onClick={() => navigate(`/objekte/${p.id}/einheiten/${u.id}`)}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-mono text-xs text-slate-500">{u.unit_number}</div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${s.className}`}>{s.label}</span>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mb-0.5">
                    {u.active_tenancy?.tenant_name || <span className="text-slate-400 italic font-normal">Leerstand</span>}
                  </div>
                  <div className="text-xs text-slate-700 mb-2">
                    {[unitTypeText(u), u.location, FLOOR_CODES[u.floor_code] || u.floor_code].filter(Boolean).join(" · ") || "—"}
                    <UnitLinkBadges unit={u} units={p.units || []} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase">Fläche</div>
                      <div className="tabular-nums">{(u.living_area || u.usable_area) ? `${u.living_area || u.usable_area} m²` : "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase">{unitLabels(u.unit_type, u.unit_kind).rooms}</div>
                      <div className="tabular-nums">{u.rooms || "—"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-slate-500 uppercase">Akt. Miete</div>
                      <div className="tabular-nums font-medium">
                        {u.active_tenancy ? eur(u.active_tenancy.rent_cold) : (u.default_rent_cold ? eur(u.default_rent_cold) : "—")}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-1.5 mt-2 pt-2 border-t border-stone-100">
                  <Button size="sm" variant="ghost" className="h-7 px-2 gap-1 text-xs" onClick={(e) => { e.stopPropagation(); setEditUnit(u); }} data-testid={`unit-edit-mobile-${u.id}`}>
                    <PencilSimple size={12} /> Bearbeiten
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 gap-1 text-xs text-rose-700 hover:text-rose-800 hover:bg-rose-50" onClick={(e) => { e.stopPropagation(); setDeleteUnit(u); }} data-testid={`unit-del-mobile-${u.id}`}>
                    <Trash size={12} /> Löschen
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: Tabelle */}
        <table className="w-full text-sm hidden md:table">
          <thead className="bg-stone-50 text-slate-700 text-left">
            <tr>
              <Th>Einheits-ID</Th><Th>Typ</Th><Th>Lage</Th><Th>Geschoss</Th>
              <Th className="text-right">Fläche</Th><Th className="text-right">Zi./Räume</Th>
              <Th>Mieter</Th>
              <Th className="text-right">Akt. Miete</Th>
              <Th>Status</Th>
              <Th className="w-32 text-right">Aktion</Th>
            </tr>
          </thead>
          <tbody>
            {(p.units || []).length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center text-slate-500">Keine Einheiten vorhanden.</td></tr>
            )}
            {(p.units || []).map((u) => {
              const s = UNIT_STATUS[u.status] || { label: u.status, className: "bg-slate-100" };
              return (
                <tr
                  key={u.id}
                  className="border-t border-stone-100 hover:bg-stone-50 transition-colors"
                  data-testid={`unit-row-${u.id}`}
                >
                  <Td className="font-mono text-slate-900 cursor-pointer" onClick={() => navigate(`/objekte/${p.id}/einheiten/${u.id}`)}>{u.unit_number}</Td>
                  <Td className="cursor-pointer" onClick={() => navigate(`/objekte/${p.id}/einheiten/${u.id}`)}>{unitTypeText(u)}<UnitLinkBadges unit={u} units={p.units || []} /></Td>
                  <Td className="cursor-pointer" onClick={() => navigate(`/objekte/${p.id}/einheiten/${u.id}`)}>{u.location || "—"}</Td>
                  <Td className="cursor-pointer" onClick={() => navigate(`/objekte/${p.id}/einheiten/${u.id}`)}>{FLOOR_CODES[u.floor_code] || u.floor_code || "—"}</Td>
                  <Td className="text-right tabular-nums">{(u.living_area || u.usable_area) ? `${u.living_area || u.usable_area} m²` : "—"}</Td>
                  <Td className="text-right tabular-nums">{u.rooms || "—"}</Td>
                  <Td className="text-xs">
                    {u.active_tenancy?.tenant_name ? (
                      <span title={`Vertrag ${u.active_tenancy.contract_number}`}>{u.active_tenancy.tenant_name}</span>
                    ) : <span className="text-slate-400">leer</span>}
                  </Td>
                  <Td className="text-right tabular-nums">
                    {u.active_tenancy
                      ? eur(u.active_tenancy.rent_cold)
                      : (u.default_rent_cold ? <span className="text-slate-400" title="Vorschlag aus Einheitsstammdaten">{eur(u.default_rent_cold)}</span> : "—")}
                  </Td>
                  <Td><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.className}`}>{s.label}</span></Td>
                  <Td className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={(e) => { e.stopPropagation(); navigate(`/objekte/${p.id}/einheiten/${u.id}`); }}
                        title="Details"
                        data-testid={`unit-open-${u.id}`}
                      >
                        <Eye size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={(e) => { e.stopPropagation(); setEditUnit(u); }}
                        title="Bearbeiten"
                        data-testid={`unit-edit-${u.id}`}
                      >
                        <PencilSimple size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                        onClick={(e) => { e.stopPropagation(); setDeleteUnit(u); }}
                        title="Löschen"
                        data-testid={`unit-del-${u.id}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
}

// ============================================================
// Stammdaten Tab — Eigentümer-Karte mit Schreiben-Funktion
// ============================================================
function OwnerCard({ propertyId, ownerContactId, onChange }) {
  return <OwnerCardInner propertyId={propertyId} ownerContactId={ownerContactId} onChange={onChange} />;
}

function WegCommunityCard({ property }) {
  const p = property;
  const [community, setCommunity] = useState(null);
  useEffect(() => {
    if (!p?.owner_contact_id) { setCommunity(null); return; }
    api.get(`/contacts/${p.owner_contact_id}`).then(({ data }) => setCommunity(data)).catch(() => setCommunity(null));
  }, [p?.owner_contact_id]);
  const isMismatch = p?.owner_contact_id && community && community.contact_type !== "weg_community";
  return (
    <div className="bg-white border border-stone-200 rounded-md p-4 shadow-sm" data-testid="weg-owner-info-card">
      {isMismatch && (
        <div
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="weg-owner-mismatch-warning"
        >
          <div className="font-semibold">Hinweis: WEG mit klassischem Eigentümer verknüpft</div>
          Dieses WEG-Objekt zeigt aktuell auf einen Einzeleigentümer
          ({community.display_name || `${community.first_name || ""} ${community.last_name || ""}`.trim() || community.company_name}).
          Bitte in den Stammdaten stattdessen eine <b>Eigentümergemeinschaft</b> zuordnen —
          Einzeleigentümer gehören an die jeweilige Einheit.
        </div>
      )}
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        Wohnungseigentümergemeinschaft (WEG)
      </div>
      <div className="text-lg font-semibold text-slate-900">
        {community?.contact_type === "weg_community" && community.company_name
          ? community.company_name
          : (p.name || p.address?.street)}
      </div>
      {community?.contact_type === "weg_community" && (
        <div className="text-sm text-slate-700 mt-1 space-y-0.5">
          {community.contact_person && (
            <div><span className="text-slate-500">Vertretung: </span>{community.contact_person}</div>
          )}
          {community.address && (
            <div className="text-slate-600">
              {[community.address.street, community.address.house_number].filter(Boolean).join(" ")}
              {(community.address.zip || community.address.city) ? ", " : ""}
              {[community.address.zip, community.address.city].filter(Boolean).join(" ")}
            </div>
          )}
          {(community.emails?.[0] || community.phones?.[0]) && (
            <div className="text-xs text-slate-500">
              {community.emails?.[0] || ""}{community.emails?.[0] && community.phones?.[0] ? " · " : ""}{community.phones?.[0] || ""}
            </div>
          )}
        </div>
      )}
      <div className="text-sm text-slate-600 mt-2">
        Eine WEG hat keinen Gesamteigentümer — die Sondereigentumseinheiten
        gehören einzelnen Eigentümern. Diese sind pro Einheit im Tab
        <b> Einheiten</b> hinterlegt.
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
        <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1">
          <span className="text-slate-500">WEG-Verwaltung: </span>
          <b className="text-slate-800">
            {p.weg_management === "external" ? "Fremd-verwaltet" :
             p.weg_management === "self" ? "Selbstverwaltet" :
             p.weg_management === "own" ? "Von uns verwaltet" : "—"}
          </b>
        </div>
        {p.external_weg_manager_contact_id && (
          <Link to={`/kontakte/${p.external_weg_manager_contact_id}`}
                className="text-emerald-700 hover:underline" data-testid="external-weg-manager-link">
            Fremd-Verwalter-Kontakt öffnen →
          </Link>
        )}
        {p.weg_manager_contact_id && !p.external_weg_manager_contact_id && (
          <Link to={`/kontakte/${p.weg_manager_contact_id}`}
                className="text-emerald-700 hover:underline">
            Fremd-Verwalter-Kontakt öffnen →
          </Link>
        )}
        {community?.contact_type === "weg_community" && (
          <Link to={`/kontakte/${community.id}`} className="text-teal-700 hover:underline">
            WEG-Stammdaten öffnen →
          </Link>
        )}
        <Link to={`/objekte/${p.id}?tab=units`} className="text-slate-600 hover:underline">
          Zu den Einheiten →
        </Link>
      </div>
    </div>
  );
}

function OwnerCardInner({ propertyId, ownerContactId, onChange }) {
  const [owner, setOwner] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [property, setProperty] = useState(null);
  const [sharesOpen, setSharesOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  // Sprint 5e — Eigentümer nebeneinander, ≥ 6 als „Eigentümergemeinschaft" ausklappbar
  const [groupExpanded, setGroupExpanded] = useState(false);
  const [ownerContacts, setOwnerContacts] = useState([]); // alle Eigentümer-Kontakte angereichert

  useEffect(() => {
    if (!ownerContactId) { setOwner(null); return; }
    api.get(`/contacts/${ownerContactId}`).then(({ data }) => setOwner(data)).catch(() => setOwner(null));
  }, [ownerContactId]);
  useEffect(() => {
    if (!propertyId) return;
    api.get(`/properties/${propertyId}`).then(({ data }) => setProperty(data)).catch(() => {});
  }, [propertyId, ownerContactId]);

  // Alle Eigentümer-Kontakte laden (primary + shares-Eigentümer)
  useEffect(() => {
    const shares = property?.ownership_shares || [];
    const allIds = new Set();
    if (ownerContactId) allIds.add(ownerContactId);
    shares.forEach((s) => { if (s.contact_id) allIds.add(s.contact_id); });
    if (allIds.size === 0) { setOwnerContacts([]); return; }
    Promise.all(Array.from(allIds).map((id) =>
      api.get(`/contacts/${id}`).then((r) => r.data).catch(() => null)
    )).then((arr) => {
      const list = arr.filter(Boolean).map((c) => {
        const share = shares.find((s) => s.contact_id === c.id);
        return {
          ...c,
          share_percent: share ? Number(share.share_percent) : null,
          is_primary: c.id === ownerContactId,
        };
      });
      // primary first, dann nach share absteigend
      list.sort((a, b) => (b.is_primary - a.is_primary) || ((b.share_percent || 0) - (a.share_percent || 0)));
      setOwnerContacts(list);
    });
  }, [property, ownerContactId]);

  if (!ownerContactId) {
    return (
      <div className="bg-stone-50 border border-stone-200 rounded-md p-4 text-sm text-slate-600">
        <div className="font-medium text-slate-700 mb-1">Eigentümer</div>
        Kein Eigentümer zugeordnet.
        {property && property.object_type !== "weg" && (
          <div className="mt-2">
            <Button size="sm" variant="outline" className="h-8" onClick={() => setMoveOpen(true)} data-testid="owner-move-btn">Eigentümer zuordnen</Button>
            <OwnerMoveDialog open={moveOpen} onOpenChange={setMoveOpen} property={property} onDone={() => onChange?.()} />
          </div>
        )}
      </div>
    );
  }
  if (!owner) {
    return <div className="bg-stone-50 border border-stone-200 rounded-md p-4 text-sm text-slate-500">Lade Eigentümer…</div>;
  }
  const displayName = (c) => c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "Eigentümer";
  const primaryName = displayName(owner);
  const shares = property?.ownership_shares || [];
  const totalOwners = ownerContacts.length;
  const isGroup = totalOwners >= 6;
  // 2026-09 · Warnung, wenn WEG-Objekt einem Nicht-WEG-Kontakt zugeordnet ist
  const isWegProp = property?.object_type === "weg";
  const wegMismatch = isWegProp && owner.contact_type !== "weg_community";

  return (
    <div className="bg-white border border-stone-200 rounded-md p-4 shadow-sm" data-testid="owner-card">
      {wegMismatch && (
        <div
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="weg-owner-mismatch-warning"
        >
          <div className="font-semibold">Hinweis: WEG-Objekt mit klassischem Eigentümer</div>
          Dieses WEG-Objekt ist einer natürlichen Person zugeordnet.
          In einer WEG sollte das Objekt der <b>Eigentümergemeinschaft</b> (Kontakttyp „WEG") zugeordnet werden;
          Einzeleigentümer gehören an die jeweilige Einheit. Bitte in den Stammdaten korrigieren.
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            {owner.contact_type === "weg_community" ? "Eigentümergemeinschaft" : "Eigentümer"}
            {owner.contact_type !== "weg_community" && owner.owner_number ? ` · #${owner.owner_number}` : ""}
            {totalOwners > 1 && <span className="ml-2 text-slate-400">· {totalOwners} Eigentümer mit Quoten</span>}
          </div>
          {/* Eigentümer-Liste nebeneinander, ab 6 als „Eigentümergemeinschaft" zusammengefasst */}
          {isGroup && !groupExpanded ? (
            <div className="flex items-center gap-3 mt-1 flex-wrap" data-testid="owner-group-collapsed">
              <span className="text-lg font-semibold text-slate-900">Eigentümergemeinschaft</span>
              <span className="text-sm text-slate-500">({totalOwners} Eigentümer)</span>
              <Button
                size="sm" variant="ghost"
                className="h-7 px-2 text-xs gap-1"
                onClick={() => setGroupExpanded(true)}
                data-testid="owner-group-expand"
              >
                Ausklappen
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-x-3 gap-y-1 mt-1 flex-wrap" data-testid="owner-list-expanded">
              {ownerContacts.length === 0 ? (
                <Link to={`/kontakte/${owner.id}`} className="text-lg font-semibold text-slate-900 hover:text-slate-700" data-testid="owner-card-link">
                  {primaryName}
                </Link>
              ) : (
                ownerContacts.map((c, i) => (
                  <span key={c.id} className="inline-flex items-center gap-1.5" data-testid={`owner-chip-${c.id}`}>
                    <Link
                      to={`/kontakte/${c.id}`}
                      data-testid={`owner-link-${c.id}`}
                      className={`hover:underline ${c.is_primary ? "text-lg font-semibold text-slate-900 hover:text-slate-700" : "text-sm text-slate-700"}`}
                    >
                      {displayName(c)}
                    </Link>
                    {c.share_percent != null && (
                      <span className="text-[11px] tabular-nums text-slate-500 font-mono">
                        {c.share_percent.toFixed(2)}%
                      </span>
                    )}
                    {i < ownerContacts.length - 1 && <span className="text-slate-300">·</span>}
                  </span>
                ))
              )}
              {isGroup && groupExpanded && (
                <Button
                  size="sm" variant="ghost"
                  className="h-7 px-2 text-xs gap-1 ml-2"
                  onClick={() => setGroupExpanded(false)}
                  data-testid="owner-group-collapse"
                >
                  Einklappen
                </Button>
              )}
            </div>
          )}
          {/* Kontaktdaten des primären Eigentümers.
              2026-02 (iter122) — Defensive Normalisierung: Kontakte können
              Emails/Phones entweder als String (Legacy) ODER als Objekt
              (neu: {email/number, label, is_primary}) liefern. Wir extrahieren
              den reinen String vor dem Rendern, sonst React-Error #31
              („Objects are not valid as a React child"). */}
          <div className="text-xs text-slate-600 mt-1.5 space-y-0.5">
            {(owner.emails || []).map((e) => (typeof e === "string" ? e : (e?.email || ""))).filter(Boolean).slice(0, 1).map((e) => (
              <div key={e} className="flex items-center gap-1.5"><EnvelopeSimple size={12} /> <a href={`mailto:${e}`} className="hover:underline">{e}</a></div>
            ))}
            {(owner.phones || []).map((p) => (typeof p === "string" ? p : (p?.number || p?.phone || ""))).filter(Boolean).slice(0, 2).map((ph) => (
              <div key={ph} className="flex items-center gap-1.5"><Phone size={12} /> {ph}</div>
            ))}
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setSharesOpen(true)} data-testid="manage-shares-btn">
            <PencilSimple size={14} /> Eigentumsquoten
          </Button>
          {!isWegProp && (
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setMoveOpen(true)} data-testid="owner-move-btn">
              <ArrowsLeftRight size={14} /> Eigentümer-Umzug
            </Button>
          )}
          <Button
            size="sm"
            className="h-8 bg-slate-900 hover:bg-slate-800 gap-1.5"
            onClick={() => setMsgOpen(true)}
            data-testid="owner-write-btn"
          >
            <PaperPlaneTilt size={14} /> Schreiben verfassen
          </Button>
        </div>
      </div>
      {shares.length > 0 && totalOwners < 6 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Eigentumsquoten</div>
          <SharesPreview shares={shares} />
        </div>
      )}
      <MessagingDialog
        open={msgOpen}
        onOpenChange={setMsgOpen}
        context={{ contact_id: owner.id, property_id: propertyId }}
        defaultSubject={`${primaryName} – Objekt`}
      />
      <OwnerHistoryList propertyId={propertyId} refreshKey={historyKey} />
      <OwnerMoveDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        property={property}
        onDone={() => { setHistoryKey((k) => k + 1); onChange?.(); api.get(`/properties/${propertyId}`).then(({ data }) => setProperty(data)); }}
      />
      <OwnershipSharesDialog
        open={sharesOpen}
        onOpenChange={setSharesOpen}
        property={property}
        primaryOwnerId={ownerContactId}
        onSaved={() => { setSharesOpen(false); onChange?.(); api.get(`/properties/${propertyId}`).then(({ data }) => setProperty(data)); }}
      />
    </div>
  );
}

function SharesPreview({ shares }) {
  const [names, setNames] = useState({});
  useEffect(() => {
    const ids = shares.map((s) => s.contact_id);
    Promise.all(ids.map((id) => api.get(`/contacts/${id}`).catch(() => null))).then((rs) => {
      const map = {};
      rs.forEach((r, i) => {
        const c = r?.data;
        if (c) map[ids[i]] = c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.id;
      });
      setNames(map);
    });
  }, [shares]);
  const sum = shares.reduce((s, x) => s + (Number(x.share_percent) || 0), 0);
  return (
    <div className="space-y-1">
      {shares.map((s, i) => (
        <div key={i} className="flex items-center justify-between text-xs">
          <span className="truncate text-slate-700">{names[s.contact_id] || (s.contact_id || "unbekannt").slice(0, 8)}</span>
          <span className="font-mono tabular-nums font-semibold text-slate-900">{Number(s.share_percent || 0).toFixed(2)}%</span>
        </div>
      ))}
      <div className={`mt-1 pt-1 border-t border-slate-100 flex items-center justify-between text-xs font-semibold ${Math.abs(sum - 100) > 0.01 ? "text-rose-700" : "text-emerald-700"}`}>
        <span>Summe</span><span className="font-mono tabular-nums">{sum.toFixed(2)}% {Math.abs(sum - 100) > 0.01 && "⚠ (sollte 100% sein)"}</span>
      </div>
    </div>
  );
}

function OwnershipSharesDialog({ open, onOpenChange, property, primaryOwnerId, onSaved }) {
  const [owners, setOwners] = useState([]);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/contacts", { params: { limit: 1000 } }).then(({ data }) => {
      setOwners(data.filter((c) => (c.roles || []).includes("owner")));
    });
    const init = (property?.ownership_shares || []).length > 0
      ? property.ownership_shares.map((s) => ({ ...s }))
      : (primaryOwnerId ? [{ contact_id: primaryOwnerId, share_percent: 100 }] : []);
    setRows(init);
  }, [open, property, primaryOwnerId]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows((rs) => [...rs, { contact_id: "", share_percent: 0 }]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const sum = rows.reduce((s, r) => s + (Number(r.share_percent) || 0), 0);
  const sumOk = Math.abs(sum - 100) < 0.01;

  const save = async () => {
    if (!property) return;
    if (rows.some((r) => !r.contact_id)) { toast.error("Bitte alle Eigentümer auswählen"); return; }
    if (!sumOk && rows.length > 0) {
      if (!await window._domivusConfirm(`Summe ist ${sum.toFixed(2)}% (nicht 100%). Trotzdem speichern?`)) return;
    }
    setSaving(true);
    try {
      await api.patch(`/properties/${property.id}`, {
        ...property,
        ownership_shares: rows.map((r) => ({ contact_id: r.contact_id, share_percent: Number(r.share_percent) })),
      });
      toast.success("Eigentumsquoten gespeichert");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  const distributeEvenly = () => {
    if (rows.length === 0) return;
    const each = Math.floor((100 / rows.length) * 100) / 100;
    const remainder = 100 - each * (rows.length - 1);
    setRows(rows.map((r, i) => ({ ...r, share_percent: i === rows.length - 1 ? Math.round(remainder * 100) / 100 : each })));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ownership-shares-dialog">
        <DialogHeader>
          <DialogTitle>Eigentumsquoten verwalten</DialogTitle>
          <DialogDescription>Mehrere Eigentümer mit Quoten — Summe muss 100% ergeben. Kostenpositionen werden anteilig im Eigentümerportal angezeigt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`share-row-${i}`}>
              <Select value={r.contact_id} onValueChange={(v) => setRow(i, { contact_id: v })}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Eigentümer wählen…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {owners.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.company_name || `${o.first_name || ""} ${o.last_name || ""}`.trim()}
                      {o.owner_number ? ` (#${o.owner_number})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" step="0.01" value={r.share_percent}
                onChange={(e) => setRow(i, { share_percent: e.target.value })}
                className="w-24 text-right font-mono"
                placeholder="%"
                data-testid={`share-percent-${i}`} />
              <span className="text-xs text-slate-500">%</span>
              <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-rose-700"
                onClick={() => removeRow(i)} data-testid={`share-remove-${i}`}>
                <Trash size={14} />
              </Button>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="share-add-row">
              <Plus size={14} className="mr-1" /> Eigentümer hinzufügen
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={distributeEvenly} disabled={rows.length === 0}>
              gleichmäßig verteilen
            </Button>
          </div>
          <div className={`mt-3 p-2.5 border rounded-md text-sm font-mono tabular-nums flex justify-between ${sumOk ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            <span>Summe der Quoten</span>
            <span className="font-bold">{sum.toFixed(2)}% {sumOk ? "✓" : "⚠"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="shares-save-btn">
            {saving ? "Speichere…" : "Quoten speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Stammdaten Tab
// ============================================================
function MasterTab({ property: p, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => masterFormFrom(p));
  useEffect(() => { setForm(masterFormFrom(p)); }, [p]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setAddr = (k, v) => setForm((s) => ({ ...s, address: { ...s.address, [k]: v } }));
  const setSecAddr = (k, v) => setForm((s) => ({ ...s, secondary_address: { ...s.secondary_address, [k]: v } }));
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    try {
      await patchProperty(p.id, {
        ...p,
        property_number: (form.property_number || "").trim() || p.property_number,
        name: form.name,
        property_type: form.property_type,
        // 2026-02 (iter117) — Fundamentale Objekt-Stammdaten
        object_type: form.object_type || "rental",
        weg_management: form.weg_management || null,
        weg_total_shares: Number(form.weg_total_shares) || 1000,
        address: form.address,
        // 2026-07 — 2. Anschrift NUR mitschicken, wenn Checkbox aktiv
        has_secondary_address: !!form.has_secondary_address,
        secondary_address: form.has_secondary_address ? form.secondary_address : null,
        secondary_address_label: form.has_secondary_address ? (form.secondary_address_label || null) : null,
        build_year: num(form.build_year),
        renovation_year: num(form.renovation_year),
        land_area: num(form.land_area),
        living_area: num(form.living_area),
        commercial_area: num(form.commercial_area),
        unit_count: num(form.unit_count),
        floor_count: num(form.floor_count),
        has_elevator: form.has_elevator,
        has_basement: form.has_basement,
        has_attic: form.has_attic,
        notes: form.notes || null,
        // iter135 — Eigentum seit (YYYY-MM-DD)
        owned_since: form.owned_since || null,
        // iter146 — Fremd-verwaltende WEG-Verwaltung als Kontakt-Referenz
        external_weg_manager_contact_id: form.external_weg_manager_contact_id || null,
        // 2026-08 — Alternatives Mietkonto pro Objekt
        rent_account_override_iban: (form.rent_account_override_iban || "").replace(/\s/g, "").toUpperCase() || null,
        rent_account_override_bank_name: form.rent_account_override_bank_name || null,
        rent_account_override_bic: form.rent_account_override_bic || null,
        rent_account_override_holder: form.rent_account_override_holder || null,
      });
      toast.success("Stammdaten gespeichert");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  // Echtzeit-Eindeutigkeitsprüfung Objekt-ID (debounced)
  const [idCheck, setIdCheck] = useState({ state: "idle", message: "" });
  useEffect(() => {
    if (!editing) return;
    const val = (form.property_number || "").trim();
    if (!val || val === p.property_number) {
      setIdCheck({ state: "idle", message: "" });
      return;
    }
    setIdCheck({ state: "checking", message: "Prüfe..." });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/properties/check-property-number", {
          params: { value: val, exclude_id: p.id },
        });
        if (data.available) {
          setIdCheck({ state: "available", message: "Verfügbar" });
        } else {
          setIdCheck({
            state: "taken",
            message: `Diese Objekt-ID wird bereits verwendet${data.used_by?.name ? ` (${data.used_by.name})` : ""}.`,
          });
        }
      } catch {
        setIdCheck({ state: "idle", message: "" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.property_number, editing, p.id, p.property_number]);

  const suggestId = async () => {
    try {
      const { data } = await api.get("/properties/suggest-property-number", {
        params: { city: form.address?.city || "", zip: form.address?.zip || "" },
      });
      if (data?.next_property_number) {
        set("property_number", data.next_property_number);
        toast.success(`Vorschlag übernommen: ${data.next_property_number}`);
      }
    } catch (e) {
      toast.error("Konnte keinen Vorschlag berechnen");
    }
  };

  // PLZ-Auto-Lookup (nur im Edit-Modus aktiv)
  const [plzHit, setPlzHit] = useState(null);
  useEffect(() => {
    if (!editing) return;
    const z = (form.address?.zip || "").replace(/\D/g, "");
    if (z.length !== 5) { setPlzHit(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/properties/lookup-plz", { params: { plz: z } });
        setPlzHit(data?.found ? data : null);
      } catch { setPlzHit(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [form.address?.zip, editing]);

  if (!editing) {
    // Bei einer WEG gehört das Objekt niemandem als Ganzes — Eigentümer sind
    // pro Sondereigentumseinheit definiert. Statt der Owner-Card zeigen wir
    // eine WEG-Info-Karte mit Verwaltungs-Angaben und Hinweis auf Einheiten.
    const isWeg = p.object_type === "weg";
    return (
      <div className="space-y-4">
        {isWeg ? (
          <WegCommunityCard property={p} />
        ) : (
          <OwnerCard propertyId={p.id} ownerContactId={p.owner_contact_id} onChange={onChange} />
        )}
        <SectionCard
          title="Stammdaten"
          action={
            <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setEditing(true)} data-testid="edit-master-btn">
              <PencilSimple size={14} /> Bearbeiten
            </Button>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            <InfoTile label="Objekt-Typ" value={
              ({ rental: "Miethaus", weg: "WEG", seg: "SEG (Sondereigentum)", mixed: "Mixed" }[p.object_type || "rental"])
            } />
            <InfoTile label="Objektart" value={PROPERTY_TYPES[p.property_type] || "—"} />
            {(p.object_type === "weg" || p.object_type === "mixed" || p.object_type === "seg") && (
              <>
                <InfoTile label="WEG-Verwaltung" value={(() => {
                  // iter146 — Konsistente Anzeige mit dem Badge im Header: bei fehlendem
                  // weg_management-Feld nutzen wir denselben object_type-basierten Fallback.
                  const mgmt = p.weg_management
                    || (p.object_type === "weg" || p.object_type === "mixed" ? "enabled"
                       : p.object_type === "seg" ? "external" : "none");
                  return ({ enabled: "Wir verwalten", external: "Fremd-verwaltet", none: "Keine WEG" }[mgmt] || "—");
                })()} />
                <InfoTile label="Gesamt-MEA (Nenner)" value={p.weg_total_shares || 1000} />
              </>
            )}
            <InfoTile label="Baujahr" value={p.build_year || "—"} />
            <InfoTile label="Sanierung" value={p.renovation_year || "—"} />
            <InfoTile label="Geschosse" value={p.floor_count || "—"} />
            <InfoTile label="Einheiten" value={p.unit_count || (p.units || []).length} />
            <InfoTile label="Grundstück" value={p.land_area ? `${p.land_area} m²` : "—"} />
            <InfoTile label="Wohnfl." value={p.living_area ? `${p.living_area} m²` : "—"} />
            <InfoTile label="Gewerbefl." value={p.commercial_area ? `${p.commercial_area} m²` : "—"} />
            <InfoTile label="Aufzug" value={ja(p.has_elevator)} />
            <InfoTile label="Keller" value={ja(p.has_basement)} />
            <InfoTile label="Dachboden" value={ja(p.has_attic)} />
            {p.object_type === "weg" ? (
              <InfoTile
                label="Eigentum & Verwaltung"
                value="Pro Einheit"
                hint="Wird bei WEG-Objekten je Einheit gepflegt"
              />
            ) : (
              <>
                <InfoTile
                  label="Eigentum seit"
                  value={p.owned_since ? new Date(p.owned_since).toLocaleDateString("de-DE") : "—"}
                  hint="Objekt gehört dem Eigentümer ab"
                />
                <InfoTile
                  label="Verwaltungsbeginn"
                  value={p.management_start ? new Date(p.management_start).toLocaleDateString("de-DE") : "—"}
                  hint={'Sollstellungen erst ab diesem Datum (optional später als „Eigentum seit")'}
                />
              </>
            )}
          </div>
          {p.notes && (
            <div className="mt-4 p-3 bg-stone-50 border border-stone-100 rounded-sm text-sm text-slate-700 whitespace-pre-wrap">{p.notes}</div>
          )}
        </SectionCard>
      </div>
    );
  }

  return (
    <SectionCard
      title="Stammdaten bearbeiten"
      action={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => { setEditing(false); setForm(masterFormFrom(p)); }}>
            <X size={14} /> Abbrechen
          </Button>
          <Button
            size="sm"
            className="gap-1.5 h-8 bg-slate-900 hover:bg-slate-800"
            onClick={save}
            disabled={idCheck.state === "taken" || idCheck.state === "checking"}
            data-testid="save-master-btn"
          >
            <FloppyDisk size={14} /> Speichern
          </Button>
        </div>
      }
    >
      {/* Objekt-ID mit Echtzeit-Validierung + Auto-Vorschlag */}
      <div className="mb-3 p-3 border border-slate-200 bg-slate-50/60 rounded-md">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Objekt-ID</Label>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 text-[11px] gap-1"
            onClick={suggestId}
            data-testid="suggest-property-number-btn"
          >
            ⚡ Vorschlag
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={form.property_number || ""}
            onChange={(e) => set("property_number", e.target.value)}
            placeholder="z. B. B-12"
            className="font-mono uppercase h-9 w-40"
            data-testid="property-number-input"
          />
          {idCheck.state === "checking" && (
            <span className="text-xs text-slate-500" data-testid="property-id-checking">Prüfe Verfügbarkeit…</span>
          )}
          {idCheck.state === "available" && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium" data-testid="property-id-available">
              ✓ Verfügbar
            </span>
          )}
          {idCheck.state === "taken" && (
            <span className="inline-flex items-center gap-1 text-xs text-rose-700 font-medium" data-testid="property-id-taken">
              ⚠ {idCheck.message || "Diese Objekt-ID wird bereits verwendet."}
            </span>
          )}
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5">
          Die Objekt-ID muss systemweit eindeutig sein und ist Teil der Mietvertrags- und Einheits-Nummern.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <F label="Objektname" className="col-span-2"><Input value={form.name} onChange={(e) => set("name", e.target.value)} /></F>
        <F label="Objektart">
          <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PROPERTY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
      </div>

      {/* 2026-02 · Verwaltungsbeginn — optional später als „Eigentum seit"
          (owned_since ist am bestehenden „Eigentum seit"-Feld weiter unten gepflegt)
          iter162 · Bei WEG ausgeblendet — wird pro Einheit gepflegt. */}
      {form.object_type !== "weg" && (
        <div className="mt-3">
          <F label={'Verwaltungsbeginn (optional, wenn später als „Eigentum seit")'}>
            <Input
              type="date"
              value={form.management_start || ""}
              onChange={(e) => set("management_start", e.target.value)}
              data-testid="property-edit-management-start"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Falls das Objekt später zur Verwaltung übernommen wurde. Wenn gesetzt
              und &gt; „Eigentum seit", gilt dieses Datum als spätere Untergrenze
              für Sollstellungen im Mietkonto.
            </div>
          </F>
        </div>
      )}
      {form.object_type === "weg" && (
        <div className="mt-3 border border-amber-200 bg-amber-50/50 rounded-md p-2.5 text-[11px] text-amber-900">
          <b>WEG:</b> „Eigentum seit" und „Verwaltungsbeginn" werden pro Sondereigentums-Einheit
          gepflegt (Einheit öffnen → Sektion <i>„Eigentum & Verwaltung"</i>).
        </div>
      )}

      {/* 2026-02 (iter117) — Fundamentale Verwaltungs-Stammdaten */}
      <div className="mt-4 p-3 border border-amber-200 bg-amber-50/40 rounded-md">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-900 mb-2">
          Verwaltungsart · Änderung wirkt sich auf Buchhaltung, BKA und WEG-JA aus
        </div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Objekt-Typ">
            <Select value={form.object_type || "rental"} onValueChange={(v) => set("object_type", v)}>
              <SelectTrigger data-testid="master-object-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rental">Miethaus (nur Vermietung)</SelectItem>
                <SelectItem value="weg">WEG (Wohnungseigentümergemeinschaft)</SelectItem>
                <SelectItem value="seg">SEG (Sondereigentumsverwaltung)</SelectItem>
                <SelectItem value="mixed">Mixed (Miete + WEG)</SelectItem>
              </SelectContent>
            </Select>
          </F>
          {(form.object_type === "weg" || form.object_type === "mixed" || form.object_type === "seg") && (
            <F label="WEG-Verwaltung">
              <Select
                value={form.weg_management || (form.object_type === "seg" ? "external" : "enabled")}
                onValueChange={(v) => set("weg_management", v)}
              >
                <SelectTrigger data-testid="master-weg-management"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="enabled">Wir verwalten die WEG</SelectItem>
                  <SelectItem value="external">Fremd-verwaltete WEG</SelectItem>
                  <SelectItem value="none">Keine WEG</SelectItem>
                </SelectContent>
              </Select>
            </F>
          )}
          {(form.object_type === "weg" || form.object_type === "mixed" || form.object_type === "seg") && (
            <F label="Gesamt-MEA (Nenner)">
              <Input
                type="number" min="1" step="1"
                value={form.weg_total_shares}
                onChange={(e) => set("weg_total_shares", e.target.value)}
                placeholder="1000"
                data-testid="master-weg-total-shares"
              />
            </F>
          )}
        </div>
        {/* iter146 — Wenn WEG-Verwaltung = external → Kontakt-Selector für den externen Verwalter */}
        {(form.weg_management === "external" || (!form.weg_management && form.object_type === "seg")) && (
          <div className="mt-3">
            <F label="Fremd-verwaltende WEG-Verwaltung (Kontakt)">
              <ExternalWegManagerSelect
                value={form.external_weg_manager_contact_id}
                onChange={(v) => set("external_weg_manager_contact_id", v)}
              />
            </F>
            <div className="text-[11px] text-slate-600 mt-1">
              Wählen Sie den Kontakt mit Rolle „Verwalter“ oder „Fremd-verwaltete WEG“. Neuen Kontakt legen Sie unter <Link to="/kontakte" className="text-emerald-700 underline">Kontakte</Link> an.
            </div>
          </div>
        )}
        {/* iter132.2 — Wenn Objekt-Typ gerade auf WEG geändert wurde und noch ein
            Person-Eigentümer hinterlegt ist, biete an, eine neue WEG als
            Eigentümer-Kontakt anzulegen. */}
        {form.object_type === "weg" && p.object_type !== "weg" && p.owner_contact_id && (
          <ConvertToWegPrompt property={p} onDone={() => window.location.reload()} />
        )}
        {(form.object_type === "weg" || form.object_type === "mixed" || form.object_type === "seg") && (
          <div className="text-[10px] text-amber-800 mt-1.5">
            Gesamt-MEA ist der Nenner aller Miteigentumsanteile im Objekt (üblich 1.000 oder 10.000).
            Einzelne Einheiten können unter „Einheiten“ ihre MEA (Zähler) und optional einen abweichenden Nenner erhalten (SEG-ETW).
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-3 mt-3">
        <F label="Straße" className="col-span-2"><Input value={form.address.street || ""} onChange={(e) => setAddr("street", e.target.value)} /></F>
        <F label="Nr."><Input value={form.address.house_number || ""} onChange={(e) => setAddr("house_number", e.target.value)} /></F>
        <F label="PLZ"><Input value={form.address.zip || ""} onChange={(e) => setAddr("zip", e.target.value)} maxLength={5} placeholder="5-stellig" data-testid="master-zip" /></F>
        <F label="Ort" className="col-span-3"><Input value={form.address.city || ""} onChange={(e) => setAddr("city", e.target.value)} /></F>
      </div>
      {plzHit && (
        <div className="mt-1 text-[11px] text-slate-600 px-1" data-testid="master-plz-hint">
          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-2 py-0.5">
            ✓ PLZ {plzHit.plz} · {plzHit.city} · Ortskennung <span className="font-mono font-semibold">{plzHit.kfz}</span>
          </span>
        </div>
      )}

      {/* 2026-07 — Zusätzliche Anschrift */}
      <div className="mt-4 border-t border-slate-100 pt-3">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.has_secondary_address}
            onChange={(e) => set("has_secondary_address", e.target.checked)}
            data-testid="master-has-secondary-address"
            className="mt-0.5 h-4 w-4 accent-slate-900"
          />
          <div>
            <div className="text-sm font-medium text-slate-800">Zusätzliche Anschrift</div>
            <div className="text-[11px] text-slate-500">
              Für Objekte mit 2 Postanschriften (Vorderhaus/Hinterhaus, Eckhäuser). Einheiten können der
              2. Anschrift zugeordnet werden — Mietverhältnisse nutzen dann automatisch die richtige Adresse.
            </div>
          </div>
        </label>
        {form.has_secondary_address && (
          <div className="mt-3 ml-6 space-y-2 border-l-2 border-emerald-200 pl-3" data-testid="master-secondary-address-block">
            <F label="Bezeichnung (frei wählbar)">
              <Input
                value={form.secondary_address_label || ""}
                onChange={(e) => set("secondary_address_label", e.target.value)}
                placeholder='z. B. „Hinterhaus", „Eingang B", „Straße Süd"'
                data-testid="master-secondary-label"
              />
            </F>
            <div className="grid grid-cols-4 gap-3">
              <F label="Straße" className="col-span-2"><Input value={form.secondary_address.street || ""} onChange={(e) => setSecAddr("street", e.target.value)} data-testid="master-secondary-street" /></F>
              <F label="Nr."><Input value={form.secondary_address.house_number || ""} onChange={(e) => setSecAddr("house_number", e.target.value)} /></F>
              <F label="PLZ"><Input value={form.secondary_address.zip || ""} onChange={(e) => setSecAddr("zip", e.target.value)} maxLength={5} /></F>
              <F label="Ort" className="col-span-3"><Input value={form.secondary_address.city || ""} onChange={(e) => setSecAddr("city", e.target.value)} data-testid="master-secondary-city" /></F>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3 mt-3">
        <F label="Baujahr"><Input type="number" value={form.build_year} onChange={(e) => set("build_year", e.target.value)} /></F>
        <F label="Sanierung"><Input type="number" value={form.renovation_year} onChange={(e) => set("renovation_year", e.target.value)} /></F>
        <F label="Geschosse"><Input type="number" value={form.floor_count} onChange={(e) => set("floor_count", e.target.value)} /></F>
        <F label="Einheiten"><Input type="number" value={form.unit_count} onChange={(e) => set("unit_count", e.target.value)} /></F>
        <F label="Grundstück (m²)"><Input type="number" value={form.land_area} onChange={(e) => set("land_area", e.target.value)} /></F>
        <F label="Wohnfl. (m²)"><Input type="number" value={form.living_area} onChange={(e) => set("living_area", e.target.value)} /></F>
        <F label="Gewerbefl. (m²)"><Input type="number" value={form.commercial_area} onChange={(e) => set("commercial_area", e.target.value)} /></F>
        {form.object_type !== "weg" && (
          <F label="Eigentum seit"
             className="col-span-2"
             hint="Buchungen davor werden automatisch dem Vor-Eigentümer zugerechnet und im Konto ausgeblendet.">
            <Input type="date" value={form.owned_since || ""}
                   onChange={(e) => set("owned_since", e.target.value)}
                   data-testid="prop-owned-since" />
          </F>
        )}
      </div>
      <div className="flex gap-5 mt-3 text-sm">
        <label className="inline-flex items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-slate-900" checked={!!form.has_elevator} onChange={(e) => set("has_elevator", e.target.checked)} />Aufzug</label>
        <label className="inline-flex items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-slate-900" checked={!!form.has_basement} onChange={(e) => set("has_basement", e.target.checked)} />Keller</label>
        <label className="inline-flex items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-slate-900" checked={!!form.has_attic} onChange={(e) => set("has_attic", e.target.checked)} />Dachboden</label>
      </div>
      <F label="Notizen" className="mt-3">
        <Textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
      </F>

      {/* 2026-08 — Alternatives Mietkonto (überschreibt Eigentümer-Default) */}
      <div className="mt-5 pt-4 border-t border-slate-200" data-testid="master-rent-override">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
          Alternatives Mietkonto (optional)
        </div>
        <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
          Standardmäßig wird in allen Mieter-Schreiben das <strong>Standard-Mietkonto des Eigentümer-Kontakts</strong> geladen
          — dieses aktualisiert sich automatisch, wenn Sie es in den Kontakteinstellungen ändern.
          Wenn Sie für dieses Objekt ein <strong>anderes Konto</strong> hinterlegen möchten, tragen Sie es hier ein.
          Es überschreibt dann das Eigentümer-Standardkonto ausschließlich für dieses Objekt.
        </p>
        <div className="grid grid-cols-4 gap-3">
          <F label="Kontoinhaber" className="col-span-2">
            <Input
              value={form.rent_account_override_holder || ""}
              onChange={(e) => set("rent_account_override_holder", e.target.value)}
              placeholder="Leer = Eigentümer-Standardkonto verwenden"
              data-testid="master-rent-holder"
            />
          </F>
          <F label="Bank" className="col-span-2">
            <Input
              value={form.rent_account_override_bank_name || ""}
              onChange={(e) => set("rent_account_override_bank_name", e.target.value)}
              data-testid="master-rent-bank"
            />
          </F>
          <F label="IBAN" className="col-span-3">
            <Input
              value={form.rent_account_override_iban || ""}
              onChange={(e) => set("rent_account_override_iban", e.target.value)}
              placeholder="DE__ ____ ____ ____ ____ __"
              className="font-mono"
              data-testid="master-rent-iban"
            />
          </F>
          <F label="BIC">
            <Input
              value={form.rent_account_override_bic || ""}
              onChange={(e) => set("rent_account_override_bic", e.target.value)}
              className="font-mono"
              data-testid="master-rent-bic"
            />
          </F>
        </div>
      </div>
    </SectionCard>
  );
}

// iter132.2 — Prompt beim Wechsel Person-Eigentümer → WEG
// iter146 — Kontakt-Selector für die Fremd-verwaltende WEG-Verwaltung.
// Zeigt Kontakte mit Rolle "manager" (Verwalter) oder "external_weg" (Fremd-WEG).
function ExternalWegManagerSelect({ value, onChange }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    // Kontakte mit Rolle "manager" ODER "external_weg" laden (client-side gemischt)
    Promise.all([
      api.get("/contacts", { params: { role: "manager", limit: 200 } }).catch(() => ({ data: [] })),
      api.get("/contacts", { params: { role: "external_weg", limit: 200 } }).catch(() => ({ data: [] })),
    ]).then(([m, w]) => {
      const merged = [...(m.data || []), ...(w.data || [])];
      // Duplikate über id filtern
      const seen = new Set();
      const dedup = merged.filter((c) => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
      setContacts(dedup);
    }).finally(() => setLoading(false));
  }, []);
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white"
      data-testid="master-external-weg-manager"
    >
      <option value="">— keine Zuweisung —</option>
      {loading && <option disabled>Lade Kontakte …</option>}
      {!loading && contacts.length === 0 && (
        <option disabled>Keine Verwalter-Kontakte. Bitte unter „Kontakte“ mit Rolle „Verwalter“ oder „Fremd-verwaltete WEG“ anlegen.</option>
      )}
      {contacts.map((c) => {
        const label = c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
        const roles = (c.roles || []).map((r) => r === "external_weg" ? "Fremd-WEG" : (r === "manager" ? "Verwalter" : r)).join(", ");
        return (
          <option key={c.id} value={c.id}>{label}{roles ? ` (${roles})` : ""}</option>
        );
      })}
    </select>
  );
}


function ConvertToWegPrompt({ property, onDone }) {
  const [busy, setBusy] = useState(false);
  const [showCustomName, setShowCustomName] = useState(false);
  const [wegName, setWegName] = useState(`WEG ${property.name || property.property_number || "Objekt"}`);

  const doConvert = async () => {
    if (!await window._domivusConfirm(
      `Neue WEG „${wegName}" als Eigentümer anlegen? ` +
      `Der bisherige Eigentümer wird als Mitglied (100 %-Anteil) hinterlegt und ` +
      `kann später auf mehrere Miteigentümer verteilt werden.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${property.id}/convert-to-weg`, {
        action: "create_new",
        weg_name: wegName.trim() || undefined,
      });
      toast.success(`WEG „${wegName}" angelegt und als Eigentümer verknüpft.`);
      if (onDone) onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "WEG-Konvertierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 p-3 rounded-md border border-sky-300 bg-sky-50">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-sky-900 mb-1">
            Eigentümer ist derzeit eine Person — WEG als Eigentümer anlegen?
          </div>
          <div className="text-xs text-sky-800">
            Bei einer WEG ist üblicherweise die Gemeinschaft (nicht eine Einzelperson)
            der Eigentümer. Wir legen einen neuen WEG-Kontakt an und verlinken den
            bisherigen Eigentümer als Mitglied. Deine Auswahl wird sofort gespeichert.
          </div>
          {showCustomName && (
            <div className="mt-2">
              <Input
                value={wegName}
                onChange={(e) => setWegName(e.target.value)}
                placeholder="Name der WEG"
                data-testid="convert-weg-name"
                className="h-8 text-xs"
              />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button
            size="sm" onClick={doConvert} disabled={busy}
            className="bg-sky-700 hover:bg-sky-800 text-white text-xs h-8"
            data-testid="convert-weg-btn"
          >
            {busy ? "Lege an…" : "Neue WEG anlegen"}
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => setShowCustomName((v) => !v)}
            className="text-xs h-7"
          >
            {showCustomName ? "Standard-Name" : "Name ändern"}
          </Button>
        </div>
      </div>
    </div>
  );
}



function masterFormFrom(p) {
  const _p = p || {};
  return {
    property_number: _p.property_number || "",
    name: _p.name || "",
    property_type: _p.property_type || "multi_family",
    // 2026-02 (iter117) — Objekttyp + WEG-Verwaltung + MEA-Nenner sind
    // fundamentale Stammdaten und werden jetzt hier zentral gepflegt.
    object_type: _p.object_type || "rental",
    weg_management: _p.weg_management || "",
    weg_total_shares: _p.weg_total_shares ?? 1000,
    // iter146 — Fremd-verwaltende WEG-Verwaltung
    external_weg_manager_contact_id: _p.external_weg_manager_contact_id || "",
    address: { ...(_p.address || {}) },
    // 2026-07 — Zusätzliche Anschrift
    has_secondary_address: !!_p.has_secondary_address,
    secondary_address_label: _p.secondary_address_label || "",
    secondary_address: _p.secondary_address
      ? { street: "", house_number: "", zip: "", city: "", country: "DE", ..._p.secondary_address }
      : { street: "", house_number: "", zip: "", city: "", country: "DE" },
    build_year: _p.build_year ?? "",
    renovation_year: _p.renovation_year ?? "",
    land_area: _p.land_area ?? "",
    living_area: _p.living_area ?? "",
    commercial_area: _p.commercial_area ?? "",
    unit_count: _p.unit_count ?? "",
    floor_count: _p.floor_count ?? "",
    has_elevator: !!_p.has_elevator,
    has_basement: !!_p.has_basement,
    has_attic: !!_p.has_attic,
    notes: _p.notes || "",
    // iter135 — Eigentums-Übernahmedatum
    owned_since: _p.owned_since || "",
    // 2026-08 — Alternatives Mietkonto (überschreibt Eigentümer-Default)
    rent_account_override_iban: _p.rent_account_override_iban || "",
    rent_account_override_bank_name: _p.rent_account_override_bank_name || "",
    rent_account_override_bic: _p.rent_account_override_bic || "",
    rent_account_override_holder: _p.rent_account_override_holder || "",
  };
}

// ============================================================
// Heizung Tab (nur Heizung, ohne Energieausweis)
// ============================================================
function HeatingTab({ property: p, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => heatingFormFrom(p));
  useEffect(() => { setForm(heatingFormFrom(p)); }, [p]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    try {
      await patchProperty(p.id, {
        ...p,
        heating_type: form.heating_type || null,
        heating_energy: form.heating_energy || null,
        hot_water_type: form.hot_water_type || null,
        fuel_tank: form.fuel_tank || "none",
      });
      toast.success("Heizung gespeichert");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  if (!editing) {
    return (
      <SectionCard
        title="Heizung"
        action={
          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setEditing(true)} data-testid="edit-heating-btn">
            <PencilSimple size={14} /> Bearbeiten
          </Button>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <InfoTile label="Heizungsart" value={HEATING_TYPES[p.heating_type] || "—"} />
          <InfoTile label="Energieträger" value={HEATING_ENERGIES[p.heating_energy] || "—"} />
          <InfoTile label="Warmwasser" value={HOT_WATER_TYPES[p.hot_water_type] || "—"} />
          <InfoTile label="Tankanlage" value={FUEL_TANKS[tankFuelOf(p) || "none"]} />
        </div>
        {tankFuelOf(p) && (
          <div className="mt-4 pt-4 border-t border-stone-200" data-testid="property-tanks-section">
            <div className="text-sm font-semibold mb-1">{tankFuelOf(p) === "lpg" ? "Gastank (Flüssiggas)" : "Öltank"} – Tankdaten, Füllstände & Lieferungen</div>
            <p className="text-xs text-slate-500 mb-3">Füllstände mit Datum und Menge hier erfassen – sie werden in der Betriebskostenabrechnung automatisch berücksichtigt.</p>
            <TankManager propertyId={p.id} fuel={tankFuelOf(p)} />
          </div>
        )}
      </SectionCard>
    );
  }
  return (
    <SectionCard
      title="Heizung bearbeiten"
      action={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => { setEditing(false); setForm(heatingFormFrom(p)); }}><X size={14} /> Abbrechen</Button>
          <Button size="sm" className="gap-1.5 h-8 bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="save-heating-btn"><FloppyDisk size={14} /> Speichern</Button>
        </div>
      }
    >
      <div className="grid grid-cols-3 gap-3">
        <F label="Heizungsart">
          <Select value={form.heating_type || "none"} onValueChange={(v) => set("heating_type", v === "none" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {Object.entries(HEATING_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
        <F label="Energieträger">
          <Select value={form.heating_energy || "none"} onValueChange={(v) => set("heating_energy", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="property-heating-energy-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {Object.entries(HEATING_ENERGIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
        <F label="Tankanlage (Öl / Flüssiggas)">
          <Select value={form.fuel_tank || "none"} onValueChange={(v) => set("fuel_tank", v)}>
            <SelectTrigger data-testid="property-fuel-tank-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(FUEL_TANKS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <div className="text-[11px] text-slate-500 mt-1">Mit Tank erscheinen Füllstände & Lieferungen hier und in der Betriebskostenabrechnung.</div>
        </F>
        <F label="Warmwasser">
          <Select value={form.hot_water_type || "none"} onValueChange={(v) => set("hot_water_type", v === "none" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {Object.entries(HOT_WATER_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
      </div>
    </SectionCard>
  );
}

// ============================================================
// Energieausweis Tab
// ============================================================
function EnergyTab({ property: p, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => energyFormFrom(p));
  useEffect(() => { setForm(energyFormFrom(p)); }, [p]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    try {
      await patchProperty(p.id, {
        ...p,
        energy_certificate_type: form.energy_certificate_type || null,
        energy_value: num(form.energy_value),
        energy_class: form.energy_class || null,
        energy_cert_valid_until: form.energy_cert_valid_until || null,
      });
      toast.success("Energieausweis gespeichert");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  if (!editing) {
    return (
      <SectionCard
        title="Energieausweis"
        action={
          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setEditing(true)} data-testid="edit-energy-btn">
            <PencilSimple size={14} /> Bearbeiten
          </Button>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <InfoTile label="Ausweistyp" value={ENERGY_CERT_TYPES[p.energy_certificate_type] || "—"} />
          <InfoTile label="kWh/(m²·a)" value={p.energy_value ?? "—"} />
          <InfoTile label="Energieklasse" value={p.energy_class || "—"} />
          <InfoTile label="Gültig bis" value={p.energy_cert_valid_until ? formatDate(p.energy_cert_valid_until) : "—"} />
        </div>
      </SectionCard>
    );
  }
  return (
    <SectionCard
      title="Energieausweis bearbeiten"
      action={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => { setEditing(false); setForm(energyFormFrom(p)); }}><X size={14} /> Abbrechen</Button>
          <Button size="sm" className="gap-1.5 h-8 bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="save-energy-btn"><FloppyDisk size={14} /> Speichern</Button>
        </div>
      }
    >
      <div className="grid grid-cols-4 gap-3">
        <F label="Ausweistyp">
          <Select value={form.energy_certificate_type || "none"} onValueChange={(v) => set("energy_certificate_type", v === "none" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {Object.entries(ENERGY_CERT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
        <F label="kWh/(m²·a)"><Input type="number" step="0.1" value={form.energy_value} onChange={(e) => set("energy_value", e.target.value)} /></F>
        <F label="Energieklasse"><Input value={form.energy_class} onChange={(e) => set("energy_class", e.target.value)} placeholder="A+, A, B, C …" /></F>
        <F label="Gültig bis"><Input type="date" value={form.energy_cert_valid_until} onChange={(e) => set("energy_cert_valid_until", e.target.value)} /></F>
      </div>
    </SectionCard>
  );
}

function heatingFormFrom(p) {
  return {
    heating_type: p.heating_type || "",
    heating_energy: p.heating_energy || "",
    hot_water_type: p.hot_water_type || "",
    fuel_tank: tankFuelOf(p) || "none",
  };
}

function energyFormFrom(p) {
  return {
    energy_certificate_type: p.energy_certificate_type || "",
    energy_value: p.energy_value ?? "",
    energy_class: p.energy_class || "",
    energy_cert_valid_until: p.energy_cert_valid_until || "",
  };
}

// ============================================================
// Versicherungen Tab
// ============================================================
function InsurancesTab({ property: p, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const remove = async (ins) => {
    if (!confirm(`Versicherung "${ins.company}" wirklich löschen?`)) return;
    try {
      await api.delete(`/properties/${p.id}/insurances/${ins.id}`);
      toast.success("Versicherung gelöscht");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <SectionCard
      title={`Versicherungen (${(p.insurances || []).length})`}
      action={
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setEditing(null)} data-testid="add-insurance-detail-btn">
              <Plus size={14} /> Versicherung hinzufügen
            </Button>
          </DialogTrigger>
          <InsuranceDialog
            propertyId={p.id}
            existing={editing}
            onSaved={() => { setOpen(false); setEditing(null); onChange(); }}
          />
        </Dialog>
      }
    >
      {(p.insurances || []).length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-8">Noch keine Versicherung erfasst.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-slate-700 text-left">
            <tr>
              <Th>Art</Th><Th>Versicherer</Th><Th>Police-Nr.</Th><Th>Beginn</Th><Th>Ende</Th><Th className="w-20">Aktion</Th>
            </tr>
          </thead>
          <tbody>
            {(p.insurances || []).map((ins) => (
              <tr key={ins.id} className="border-t border-stone-100 hover:bg-stone-50 cursor-pointer" data-testid={`insurance-detail-row-${ins.id}`} onClick={() => { setEditing(ins); setOpen(true); }}>
                <Td>{INSURANCE_TYPES[ins.insurance_type] || ins.insurance_type || "—"}</Td>
                <Td className="font-medium">{ins.company}</Td>
                <Td>{ins.policy_number || "—"}</Td>
                <Td>{formatDate(ins.contract_start)}</Td>
                <Td>{formatDate(ins.contract_end)}</Td>
                <Td onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(ins); setOpen(true); }}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => remove(ins)}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionCard>
  );
}

function InsuranceDialog({ propertyId, existing, onSaved }) {
  const [form, setForm] = useState(() => ({
    company: existing?.company || "",
    policy_number: existing?.policy_number || "",
    insurance_type: existing?.insurance_type || "building",
    contract_start: existing?.contract_start || "",
    contract_end: existing?.contract_end || "",
    notes: existing?.notes || "",
  }));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      company: form.company,
      policy_number: form.policy_number || null,
      insurance_type: form.insurance_type || null,
      contract_start: form.contract_start || null,
      contract_end: form.contract_end || null,
      notes: form.notes || null,
    };
    try {
      if (existing?.id) {
        await api.patch(`/properties/${propertyId}/insurances/${existing.id}`, payload);
        toast.success("Versicherung aktualisiert");
      } else {
        await api.post(`/properties/${propertyId}/insurances`, payload);
        toast.success("Versicherung angelegt");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{existing ? "Versicherung bearbeiten" : "Neue Versicherung"}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <F label="Art">
            <Select value={form.insurance_type} onValueChange={(v) => set("insurance_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(INSURANCE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Police-Nr."><Input value={form.policy_number} onChange={(e) => set("policy_number", e.target.value)} /></F>
        </div>
        <F label="Versicherer"><Input required value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="insurance-dialog-company" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Vertrag ab"><Input type="date" value={form.contract_start} onChange={(e) => set("contract_start", e.target.value)} /></F>
          <F label="Vertrag bis"><Input type="date" value={form.contract_end} onChange={(e) => set("contract_end", e.target.value)} /></F>
        </div>
        <F label="Notizen"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></F>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="insurance-dialog-save">{existing ? "Speichern" : "Anlegen"}</Button>
        </DialogFooter>
      </form>
      {existing?.id && (
        <DocumentsBlock
          propertyId={propertyId}
          insuranceId={existing.id}
          category="Versicherung"
          title="Versicherungsdokumente"
        />
      )}
    </DialogContent>
  );
}

// ============================================================
// DocumentsBlock — reusable Liste + Upload (per Property + optional Insurance)
// ============================================================
function DocumentsBlock({ propertyId, insuranceId, contactId, unitId, category, title = "Dokumente" }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    const params = {};
    if (propertyId) params.property_id = propertyId;
    if (insuranceId) params.insurance_id = insuranceId;
    if (contactId) params.contact_id = contactId;
    if (category) params.category = category;
    const { data } = await api.get("/documents", { params });
    setDocs(data.filter((d) => (unitId ? d.linked_unit_id === unitId : true)));
  };

  useEffect(() => { load(); }, [propertyId, insuranceId, contactId, unitId, category]);

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    if (category) fd.append("category", category);
    if (propertyId) fd.append("linked_property_id", propertyId);
    if (insuranceId) fd.append("linked_insurance_id", insuranceId);
    if (contactId) fd.append("linked_contact_id", contactId);
    if (unitId) fd.append("linked_unit_id", unitId);
    setUploading(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Datei hochgeladen");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, docTitle) => {
    const ok = await confirm({
      title: "Dokument löschen?",
      description: docTitle ? `Das Dokument "${docTitle}" wird endgültig gelöscht.` : "Das Dokument wird endgültig gelöscht.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const downloadUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/download`;

  return (
    <div className="border-t border-stone-200 pt-4 mt-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-900 hover:bg-slate-800 text-white rounded-md cursor-pointer" data-testid="document-upload-btn">
          <UploadSimple size={14} />
          {uploading ? "Lade hoch…" : "Hochladen"}
          <input type="file" className="hidden" disabled={uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }} />
        </label>
      </div>
      {docs.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-3">Noch keine Dokumente.</div>
      ) : (
        <ul className="divide-y divide-stone-100 border border-stone-200 rounded-md overflow-hidden">
          {docs.map((d) => (
            <li key={d.id} className="px-3 py-2 flex items-center gap-2 text-sm hover:bg-stone-50" data-testid={`document-row-${d.id}`}>
              <FileText size={14} className="text-slate-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate text-slate-900">{d.title}</div>
                <div className="text-[11px] text-slate-500">
                  {d.category} · {formatDate(d.created_at)} · {d.original_filename}
                </div>
              </div>
              <button
                className="p-1.5 rounded hover:bg-stone-100 text-slate-600"
                onClick={() => setPreviewDoc(d)}
                title="Vorschau"
                data-testid={`doc-view-${d.id}`}
              >
                <Eye size={14} />
              </button>
              <a
                href={downloadUrl(d.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded hover:bg-stone-100"
                title="Herunterladen"
              >
                <DownloadSimple size={14} />
              </a>
              <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => handleDelete(d.id, d.title)} title="Löschen" data-testid={`property-doc-del-${d.id}`}>
                <Trash size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {previewDoc && (
        <DocumentPreviewDrawer
          docId={previewDoc.id}
          metaOverride={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// Zuständiger Betreuer Tab
// ============================================================
function CaretakerTab({ property: p, onChange }) {
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(p.caretaker_user_id || "");
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    api.get("/users/staff-list").then(({ data }) => setStaff(data)).catch(() => setStaff([]));
  }, []);
  useEffect(() => { setSelected(p.caretaker_user_id || ""); }, [p.caretaker_user_id]);

  const current = staff.find((s) => s.id === p.caretaker_user_id);

  const save = async () => {
    try {
      await patchProperty(p.id, { ...p, caretaker_user_id: selected || null });
      toast.success("Betreuer gespeichert");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <SectionCard
      title="Zuständiger Betreuer"
      action={
        editing ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => { setEditing(false); setSelected(p.caretaker_user_id || ""); }}>
              <X size={14} /> Abbrechen
            </Button>
            <Button size="sm" className="gap-1.5 h-8 bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="save-caretaker-btn">
              <FloppyDisk size={14} /> Speichern
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setEditing(true)} data-testid="edit-caretaker-btn">
            <PencilSimple size={14} /> {p.caretaker_user_id ? "Ändern" : "Zuweisen"}
          </Button>
        )
      }
    >
      {editing ? (
        <F label="Benutzer auswählen">
          <Select value={selected || "none"} onValueChange={(v) => setSelected(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="caretaker-select"><SelectValue placeholder="Benutzer wählen" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Kein Betreuer —</SelectItem>
              {staff.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name} <span className="text-slate-400">· {ROLE_LABELS[u.role] || u.role}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </F>
      ) : current ? (
        <button
          type="button"
          onClick={() => setDetailOpen(true)}
          className="w-full text-left flex items-center gap-4 p-3 bg-stone-50 border border-stone-100 rounded-md hover:bg-stone-100 transition-colors"
          data-testid="caretaker-card-clickable"
        >
          <div className="h-12 w-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold text-lg shrink-0">
            {(current.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="font-medium text-slate-900">{current.name}</div>
            <div className="text-xs text-slate-500">{ROLE_LABELS[current.role] || current.role}</div>
            <div className="text-xs text-slate-500">{current.email}</div>
          </div>
          <ArrowRight size={16} className="text-slate-400" />
        </button>
      ) : (
        <div className="text-sm text-slate-500 text-center py-8">Kein Betreuer zugewiesen.</div>
      )}
      <CaretakerDetailDialog open={detailOpen} onOpenChange={setDetailOpen} user={current} />
    </SectionCard>
  );
}

function CaretakerDetailDialog({ open, onOpenChange, user }) {
  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{user.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-stone-50 border border-stone-100 rounded-md">
            <div className="h-14 w-14 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold text-xl shrink-0">
              {(user.name || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-medium text-slate-900">{user.name}</div>
              <div className="text-xs text-slate-500">{ROLE_LABELS[user.role] || user.role}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700"><EnvelopeSimple size={14} className="text-slate-500" /> <a href={`mailto:${user.email}`} className="hover:underline">{user.email}</a></div>
            {user.phone && <div className="flex items-center gap-2 text-slate-700"><Phone size={14} className="text-slate-500" /> {user.phone}</div>}
            {user.extension && <div className="text-xs text-slate-500 pl-6">Durchwahl: {user.extension}</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Zähler & Werte Tab
// ============================================================
function MetersTab({ property: p, onChange }) {
  const [openMeter, setOpenMeter] = useState(false);
  const [editing, setEditing] = useState(null);
  const [readingsFor, setReadingsFor] = useState(null);
  const [changeFor, setChangeFor] = useState(null);
  const confirm = useConfirm();
  const cancelChange = async (m) => {
    try { await api.delete(`/properties/${p.id}/meters/${m.id}/change`); toast.success("Geplanter Wechsel abgebrochen"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const meters = p.meters || [];
  const unitsById = useMemo(() => {
    const m = {};
    (p.units || []).forEach((u) => { m[u.id] = u; });
    return m;
  }, [p.units]);

  const removeMeter = async (m) => {
    const ok = await confirm({
      title: "Zähler löschen?",
      description: `Der Zähler "${m.meter_number}" wird inklusive aller ${(m.readings || []).length} Ablesungen endgültig gelöscht.`,
      confirmLabel: "Endgültig löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/properties/${p.id}/meters/${m.id}`);
      toast.success("Zähler gelöscht");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title={`Zähler (${meters.length})`}
        action={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="gap-1.5 h-8 border-teal-300 text-teal-800 hover:bg-teal-50"
                    onClick={() => window.location.href = `/zaehler/erfassen?property_id=${p.id}`}
                    data-testid="scanner-open-btn">
              <Camera size={14} weight="bold" /> Scanner
            </Button>
            <Dialog open={openMeter} onOpenChange={(v) => { setOpenMeter(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setEditing(null)} data-testid="add-meter-detail-btn">
                  <Plus size={14} /> Zähler hinzufügen
                </Button>
              </DialogTrigger>
              {openMeter && (
                <MeterDialog
                  key={editing?.id || "new"}
                  propertyId={p.id}
                  units={p.units || []}
                  existing={editing}
                  onSaved={() => { setOpenMeter(false); setEditing(null); onChange(); }}
                />
              )}
            </Dialog>
          </div>
        }
      >
        {meters.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-8">Noch keine Zähler erfasst.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-slate-700 text-left">
              <tr>
                <Th>Zählernummer</Th><Th>Art</Th><Th>Einheit (Mess)</Th><Th>Standort</Th><Th>Zugeordnete Einheit</Th><Th>Letzte Ablesung</Th><Th className="w-32">Aktion</Th>
              </tr>
            </thead>
            <tbody>
              {meters.map((m) => {
                const readings = (m.readings || []).slice().sort((a, b) => (a.reading_date < b.reading_date ? 1 : -1));
                const last = readings[0];
                return (
                  <tr key={m.id} className={`border-t border-stone-100 hover:bg-stone-50 ${m.active === false ? "opacity-60" : ""}`} data-testid={`meter-detail-row-${m.id}`}>
                    <Td className="font-mono">{m.meter_number}
                      {m.active === false && <div className="font-sans text-[10px] text-slate-500" data-testid={`meter-removed-${m.id}`}>ausgebaut {m.removed_at ? formatDate(m.removed_at) : ""}</div>}
                      {m.planned_change && <div className="font-sans text-[10px] text-sky-700" data-testid={`meter-planned-${m.id}`}>Wechsel geplant {formatDate(m.planned_change.change_date)} → {m.planned_change.new_meter_number}
                        <button type="button" className="ml-1 underline" onClick={() => cancelChange(m)} data-testid={`meter-planned-cancel-${m.id}`}>abbrechen</button></div>}
                      {(() => { const c = calibrationInfo(m); return c ? <div className={`font-sans text-[10px] ${c.expired ? "text-rose-700 font-semibold" : c.soon ? "text-amber-700" : "text-slate-500"}`} data-testid={`meter-calibration-${m.id}`}>
                        Eichung {c.label}{c.until ? ` · gültig bis Ende ${c.until}${c.expired ? " (abgelaufen)" : ""}` : ""}</div> : null; })()}
                    </Td>
                    <Td>{METER_TYPES[m.meter_type] || "—"}</Td>
                    <Td>{m.measurement_unit || "—"}</Td>
                    <Td>{m.location || "—"}</Td>
                    <Td>{m.unit_id ? (() => { const u = unitsById[m.unit_id]; return u ? (
                      <div data-testid={`meter-unit-${m.id}`}><div className="font-medium text-slate-900">{u.unit_number || "—"}{u.unit_type ? <span className="ml-1 text-[11px] font-normal text-slate-500">{UNIT_TYPES[u.unit_type] || u.unit_type}</span> : null}</div>
                        {(u.location || u.floor_code) && <div className="text-[11px] text-slate-500">{u.location || u.floor_code}</div>}
                        {(m.unit_ids || []).filter((x) => unitsById[x]).map((x) => <div key={x} className="text-[11px] text-teal-800" data-testid={`meter-extra-unit-label-${m.id}-${x}`}>+ {unitsById[x].unit_number}</div>)}</div>) : "—"; })() : <span className="text-slate-400">Objekt-Zähler</span>}</Td>
                    <Td className="tabular-nums">
                      {last ? `${formatDate(last.reading_date)} · ${last.value} ${m.measurement_unit || ""}` : <span className="text-slate-400">—</span>}
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReadingsFor(m)} data-testid={`meter-readings-btn-${m.id}`}>
                          Werte
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(m); setOpenMeter(true); }} data-testid={`meter-edit-btn-${m.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        {m.active !== false && !m.planned_change && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => setChangeFor(m)} data-testid={`meter-change-btn-${m.id}`} title="Zählerwechsel">Wechsel</Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => removeMeter(m)} data-testid={`meter-del-btn-${m.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </SectionCard>

      {changeFor && (
        <Dialog open={!!changeFor} onOpenChange={(v) => !v && setChangeFor(null)}>
          <MeterChangeDialog propertyId={p.id} meter={changeFor} onDone={() => { setChangeFor(null); onChange(); }} />
        </Dialog>
      )}
      {readingsFor && (
        <Dialog open={!!readingsFor} onOpenChange={(v) => !v && setReadingsFor(null)}>
          <ReadingsDialog
            propertyId={p.id}
            meter={readingsFor}
            onSaved={() => { setReadingsFor(null); onChange(); }}
            onClose={() => setReadingsFor(null)}
          />
        </Dialog>
      )}
    </div>
  );
}

function MeterDialog({ propertyId, units, existing, onSaved }) {
  const [form, setForm] = useState(() => ({
    meter_number: existing?.meter_number || "",
    meter_type: existing?.meter_type || "heat_quantity",
    location: existing?.location || "",
    unit_id: existing?.unit_id || "",
    measurement_unit: existing?.measurement_unit || METER_UNIT_SUGGESTION[existing?.meter_type || "heat_quantity"] || "",
    installed_date: existing?.installed_date || "",
    notes: existing?.notes || "",
    unit_ids: existing?.unit_ids || [],
    calibration_date: existing?.calibration_date || "",
  }));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (form.calibration_date && !/^\d{4}(-\d{2}-\d{2})?$/.test(form.calibration_date)) { toast.error("Eichdatum: bitte vierstelliges Jahr (z. B. 2024) oder ein Datum angeben"); return; }
    const payload = {
      meter_number: form.meter_number,
      meter_type: form.meter_type,
      location: form.location || null,
      unit_id: form.unit_id || null,
      measurement_unit: form.measurement_unit || null,
      installed_date: form.installed_date || null,
      notes: form.notes || null,
      unit_ids: form.unit_id ? form.unit_ids.filter((x) => x !== form.unit_id) : [],
      calibration_date: form.calibration_date || null,
      readings: existing?.readings || [],
    };
    try {
      if (existing?.id) {
        await api.patch(`/properties/${propertyId}/meters/${existing.id}`, payload);
        toast.success("Zähler aktualisiert");
      } else {
        await api.post(`/properties/${propertyId}/meters`, payload);
        toast.success("Zähler angelegt");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader><DialogTitle>{existing ? "Zähler bearbeiten" : "Neuer Zähler"}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <F label="Zählerart">
            <Select value={form.meter_type} onValueChange={(v) => setForm((s) => ({ ...s, meter_type: v, measurement_unit: METER_UNIT_SUGGESTION[v] ?? s.measurement_unit }))}>
              <SelectTrigger data-testid="meter-dialog-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(METER_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Zählernummer"><Input required value={form.meter_number} onChange={(e) => set("meter_number", e.target.value)} data-testid="meter-dialog-number" /></F>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Mess-Einheit"><Input value={form.measurement_unit} onChange={(e) => set("measurement_unit", e.target.value)} placeholder="kWh / m³ / …" /></F>
          <F label="Einbaudatum"><DatePicker value={form.installed_date} onChange={(v) => set("installed_date", v)} data-testid="meter-dialog-installed" /></F>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Standort"><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="z. B. Keller / Wohnung XY" /></F>
          <CalibrationField value={form.calibration_date} onChange={(v) => set("calibration_date", v)} />
        </div>
        <F label="Zuordnung Einheit (optional)">
          <Select value={form.unit_id || "none"} onValueChange={(v) => set("unit_id", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="meter-dialog-unit"><SelectValue placeholder="— Objekt-Zähler —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Objekt-Zähler (keine Einheit) —</SelectItem>
              {units.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.unit_number} · {u.location || ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </F>
        <UnitMultiSelect units={units} primary={form.unit_id} value={form.unit_ids} onChange={(v) => set("unit_ids", v)} />
        <F label="Notizen"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></F>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="meter-dialog-save">{existing ? "Speichern" : "Anlegen"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function ReadingsDialog({ propertyId, meter, onSaved, onClose }) {
  const [readings, setReadings] = useState(() => (meter.readings || []).slice().sort((a, b) => (a.reading_date < b.reading_date ? 1 : -1)));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const add = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!date || value === "") {
      toast.error("Datum und Wert sind Pflichtfelder");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/meters/${meter.id}/readings`, {
        reading_date: date,
        value: Number(value),
        notes: notes || null,
      });
      setReadings((prev) => [data, ...prev].sort((a, b) => (a.reading_date < b.reading_date ? 1 : -1)));
      setValue("");
      setNotes("");
      toast.success("Ablesung gespeichert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: "Ablesung löschen?",
      description: `Ablesung vom ${formatDate(r.reading_date)} wird endgültig gelöscht.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/properties/${propertyId}/meters/${meter.id}/readings/${r.id}`);
      setReadings((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Ablesung gelöscht");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>
          Ablesungen – {METER_TYPES[meter.meter_type] || meter.meter_type} <span className="font-mono text-slate-500">#{meter.meter_number}</span>
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={add} className="grid grid-cols-12 gap-2 items-end p-3 bg-stone-50 rounded-md border border-stone-200">
        <div className="col-span-3">
          <Label className="text-xs">Datum</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required data-testid="reading-date" />
        </div>
        <div className="col-span-3">
          <Label className="text-xs">Wert ({meter.measurement_unit || "—"})</Label>
          <Input type="number" step="0.001" value={value} onChange={(e) => setValue(e.target.value)} required data-testid="reading-value" />
        </div>
        <div className="col-span-4">
          <Label className="text-xs">Notiz (optional)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="z. B. Jahresablesung" />
        </div>
        <div className="col-span-2">
          <Button type="submit" disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 gap-1" data-testid="reading-save-btn">
            <Plus size={14} /> Eintragen
          </Button>
        </div>
      </form>

      <div className="mt-3 border border-stone-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-slate-700 text-left">
            <tr><Th>Datum</Th><Th className="text-right">Wert</Th><Th>Notiz</Th><Th className="w-12"></Th></tr>
          </thead>
          <tbody>
            {readings.length === 0 && (
              <tr><td colSpan={4} className="p-4 text-center text-slate-500 text-sm">Noch keine Ablesungen.</td></tr>
            )}
            {readings.map((r) => (
              <tr key={r.id} className="border-t border-stone-100" data-testid={`reading-row-${r.id}`}>
                <Td>{formatDate(r.reading_date)}</Td>
                <Td className="text-right tabular-nums">{Number(r.value).toLocaleString("de-DE")} {meter.measurement_unit || ""}</Td>
                <Td className="text-slate-600 text-xs">{r.notes || ""}</Td>
                <Td>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => remove(r)}>
                    <Trash size={14} />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Schließen</Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ============================================================
// Unit dialog (Create + Edit)
// ============================================================
function UnitCoOwnersEditor({ form, setForm, ownerCandidates }) {
  const shares = form.ownership_shares || [];
  const primaryId = form.owner_contact_id || "";
  // Available candidates: everyone except already-picked contact IDs (and the primary owner)
  const takenIds = new Set(shares.map((s) => s.contact_id).filter(Boolean));
  if (primaryId) takenIds.add(primaryId);
  const available = ownerCandidates.filter((c) => c && c.id && !takenIds.has(c.id));

  const setShare = (idx, patch) => {
    setForm((s) => {
      const arr = [...(s.ownership_shares || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...s, ownership_shares: arr };
    });
  };
  const removeShare = (idx) => {
    setForm((s) => ({ ...s, ownership_shares: (s.ownership_shares || []).filter((_, i) => i !== idx) }));
  };
  const addShare = () => {
    setForm((s) => ({ ...s, ownership_shares: [...(s.ownership_shares || []), { contact_id: "", share_percent: 0 }] }));
  };

  // Auto-Berechnung: Haupt-Eigentümer erhält Restanteil
  const totalCoOwners = shares.reduce((sum, s) => sum + (Number(s.share_percent) || 0), 0);
  const primaryShare = Math.max(0, 100 - totalCoOwners);
  const sumTotal = primaryShare + totalCoOwners;
  const sumOk = Math.abs(sumTotal - 100) < 0.001;

  const nameFor = (cid) => {
    const c = ownerCandidates.find((x) => x.id === cid);
    if (!c) return cid || "—";
    return c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || c.company || cid;
  };

  return (
    <div className="mt-3 border border-slate-200 bg-slate-50/40 rounded-md p-3" data-testid="co-owners-editor">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">
            Miteigentümer &amp; Quoten
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Optional. Wenn leer, gehört die Einheit zu 100 % dem Haupt-Eigentümer.
            Der Rest bis 100 % wird dem Haupt-Eigentümer automatisch zugeordnet.
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={addShare}
          disabled={!primaryId || available.length === 0}
          data-testid="co-owner-add"
        >
          <Plus size={12} /> Miteigentümer
        </Button>
      </div>

      {/* Primary owner row (read-only, share auto-computed) */}
      {primaryId ? (
        <div className="flex items-center gap-2 py-1.5 border-b border-slate-200 last:border-b-0">
          <div className="flex-1 text-sm text-slate-900 truncate">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-2">Haupt</span>
            {nameFor(primaryId)}
          </div>
          <div className="text-sm font-mono text-slate-900 tabular-nums w-24 text-right" data-testid="co-owner-primary-share">
            {primaryShare.toFixed(2)} %
          </div>
          <div className="w-8" />
        </div>
      ) : (
        <div className="text-xs text-slate-500 py-2 italic">Bitte zuerst einen Haupt-Eigentümer wählen.</div>
      )}

      {shares.map((s, idx) => {
        const candidates = ownerCandidates.filter(
          (c) => c && c.id && (c.id === s.contact_id || (c.id !== primaryId && !shares.some((x, i) => i !== idx && x.contact_id === c.id))),
        );
        return (
          <div key={idx} className="flex items-center gap-2 py-1.5 border-b border-slate-200 last:border-b-0">
            <div className="flex-1 min-w-0">
              <SearchableCombobox
                value={s.contact_id || ""}
                onChange={(v) => setShare(idx, { contact_id: v })}
                options={candidates.map((c) => ({
                  id: c.id,
                  label: `${c.owner_number ? `[${c.owner_number}] ` : ""}${c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || c.id}`,
                  sublabel: [c.emails?.[0], c.phones?.[0]].filter(Boolean).join(" · ") || undefined,
                  keywords: [c.owner_number, c.first_name, c.last_name, c.company_name, ...(c.emails || []), ...(c.phones || [])].filter(Boolean).join(" "),
                }))}
                placeholder="Miteigentümer suchen…"
                testId={`co-owner-select-${idx}`}
              />
            </div>
            <div className="w-24">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={s.share_percent === "" || s.share_percent == null ? "" : s.share_percent}
                onChange={(e) => setShare(idx, { share_percent: e.target.value === "" ? "" : Number(e.target.value) })}
                className="h-9 text-right font-mono"
                data-testid={`co-owner-share-${idx}`}
              />
            </div>
            <span className="text-xs text-slate-500 -ml-1">%</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-rose-600"
              onClick={() => removeShare(idx)}
              data-testid={`co-owner-remove-${idx}`}
            >
              <Trash size={14} />
            </Button>
          </div>
        );
      })}

      {/* Summe */}
      <div className={`mt-2 pt-2 border-t border-slate-200 flex items-center justify-between text-xs ${sumOk ? "text-slate-600" : "text-rose-700 font-semibold"}`}
           data-testid="co-owners-sum">
        <span>Summe aller Anteile</span>
        <span className="font-mono tabular-nums">
          {sumTotal.toFixed(2)} %{!sumOk && " · muss 100 ergeben"}
        </span>
      </div>
    </div>
  );
}

// ============================================================
// Unit dialog (Create + Edit)
// ============================================================
function UnitDialog({ propertyId, propertyNumber, objectType, property, existing = null, onSaved }) {
  const isEdit = !!existing;
  const isWEG = objectType === "weg" || objectType === "mixed";
  // 2026-07 — Zusätzliche Anschrift des Objekts?
  const hasSecondaryAddress = !!(property && property.has_secondary_address);
  const secondaryLabel = property?.secondary_address_label || "2. Anschrift";
  const [ownerCandidates, setOwnerCandidates] = useState([]);
  // 2026-02 (iter116) — Für SEG-ETW: Liste aller WEG-Objekte im Mandanten (zur Verknüpfung)
  const [wegPropertyCandidates, setWegPropertyCandidates] = useState([]);
  // 2026-02 (iter120) — Für SEG-ETW: Liste der Fremd-verwalteten WEG-Kontakte
  const [externalWegCandidates, setExternalWegCandidates] = useState([]);
  useEffect(() => {
    // Eigentümer-Kandidaten laden — sowohl für WEG/Mixed als auch für SEG-Einheiten
    // (Bugfix iter120: Vorher wurde bei SEG das Dropdown nie befüllt.)
    if (!isWEG && objectType !== "seg") return;
    api.get("/contacts", { params: { role: "owner", limit: 500 } })
      .then((r) => setOwnerCandidates(r.data || [])).catch(() => {});
  }, [isWEG, objectType]);
  useEffect(() => {
    if (objectType !== "seg") return;
    api.get("/properties")
      .then((r) => {
        const all = r.data || [];
        setWegPropertyCandidates(all.filter((p) => (p.object_type === "weg" || p.object_type === "mixed") && p.id !== propertyId));
      })
      .catch(() => setWegPropertyCandidates([]));
    // Fremd-verwaltete WEGs als Kontakte laden
    api.get("/contacts", { params: { role: "external_weg", limit: 500 } })
      .then((r) => setExternalWegCandidates(r.data || []))
      .catch(() => setExternalWegCandidates([]));
  }, [objectType, propertyId]);
  const [form, setForm] = useState(() => existing ? unitFormFrom(existing) : {
    ...commercialFormFrom({}),
    unit_kind: 1, floor_code: "00", additional_floor_codes: [], apartment_subtype: "", position: 0,
    side: "", location: "", living_area: "", usable_area: "", rooms: "",
    bedrooms: "", bathrooms: "", toilets: "",
    has_separate_wc: false, has_kitchen: false,
    has_balcony: false, balcony_area: "", loggia_area: "",
    has_terrace: false, terrace_area: "",
    has_garden: false, garden_area: "",
    has_basement_room: false, cellar_area: "", cellar_number: "",
    has_attic_room: false, has_elevator: null,
    furnishing: "", quality_standard: "",
    parking_spots: "", parking_type: "",
    garage_kind: "", garage_plate: "", garage_length: "", garage_width: "", garage_height: "",
    garage_door_type: "", has_power: null, has_wallbox: null, wallbox_kw: "",
    floor_covering: "", heating_type: "", hot_water_type: "",
    last_renovation_year: "",
    default_rent_cold: "", default_utilities_advance: "", default_heating_advance: "",
    mea_share: "",
    weg_share: "",
    weg_share_denominator: "",
    is_etw_in_seg: objectType === "seg",
    parent_weg_property_id: "",
    parent_weg_contact_id: "",
    is_sondereigentum: true,
    owner_contact_id: "",
    ownership_shares: [],
    status: "vacant",
    unit_number_override: existing?.unit_number_override || "",
    notes: "",
    // 2026-07 — Adress-Variante der Einheit (primary/secondary)
    address_variant: "primary",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const str = (v) => (v === "" ? null : v);

  const toggleAdditionalFloor = (code) => {
    setForm((s) => {
      const cur = s.additional_floor_codes || [];
      if (cur.includes(code)) return { ...s, additional_floor_codes: cur.filter((x) => x !== code) };
      return { ...s, additional_floor_codes: [...cur, code] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      property_id: propertyId,
      unit_kind: Number(form.unit_kind),
      floor_code: form.floor_code,
      additional_floor_codes: (form.additional_floor_codes || []).filter((c) => c !== form.floor_code),
      apartment_subtype: form.apartment_subtype || null,
      position: Number(form.position) || (isEdit ? 1 : 0),
      side: form.side || null,
      location: form.location,
      living_area: num(form.living_area),
      usable_area: num(form.usable_area),
      rooms: num(form.rooms),
      bedrooms: num(form.bedrooms),
      bathrooms: num(form.bathrooms),
      has_separate_wc: form.has_separate_wc,
      has_kitchen: form.has_kitchen,
      has_balcony: form.has_balcony,
      balcony_area: num(form.balcony_area),
      has_terrace: form.has_terrace,
      has_garden: form.has_garden,
      has_basement_room: form.has_basement_room,
      has_attic_room: form.has_attic_room,
      parking_spots: num(form.parking_spots),
      parking_type: str(form.parking_type),
      floor_covering: str(form.floor_covering),
      heating_type: str(form.heating_type),
      hot_water_type: str(form.hot_water_type),
      last_renovation_year: num(form.last_renovation_year),
      default_rent_cold: num(form.default_rent_cold),
      default_utilities_advance: num(form.default_utilities_advance),
      default_heating_advance: num(form.default_heating_advance),
      mea_share: num(form.mea_share),
      status: form.status,
      // VM-14d — erweiterte Ausstattungs-Felder
      toilets: num(form.toilets),
      loggia_area: num(form.loggia_area),
      terrace_area: num(form.terrace_area),
      garden_area: num(form.garden_area),
      cellar_area: num(form.cellar_area),
      cellar_number: str(form.cellar_number),
      has_elevator: form.has_elevator,
      furnishing: str(form.furnishing),
      quality_standard: str(form.quality_standard),
      // Garage-spezifisch (werden vom Server ignoriert wenn irrelevant)
      garage_kind: str(form.garage_kind),
      garage_plate: str(form.garage_plate),
      garage_length: num(form.garage_length),
      garage_width: num(form.garage_width),
      garage_height: num(form.garage_height),
      garage_door_type: str(form.garage_door_type),
      has_power: form.has_power,
      has_wallbox: form.has_wallbox,
      wallbox_kw: num(form.wallbox_kw),
      // Sprint 5c — Manueller Override der Einheits-Nummer
      unit_number_override: form.unit_number_override || null,
      notes: str(form.notes),
      // WEG Phase 1 — Miteigentumsanteile, Sondereigentum, Eigentümer-Zuordnung
      weg_share: num(form.weg_share),
      // 2026-02 (iter116) — MEA-Nenner-Override (SEG-ETW mit abweichender WEG-Basis)
      weg_share_denominator: num(form.weg_share_denominator),
      is_etw_in_seg: !!form.is_etw_in_seg,
      parent_weg_property_id: str(form.parent_weg_property_id),
      parent_weg_contact_id: str(form.parent_weg_contact_id),
      is_sondereigentum: form.is_sondereigentum !== false,
      owner_contact_id: str(form.owner_contact_id),
      // 2026-09 — Miteigentümer/Quoten (nur zulässige, valide Einträge)
      ownership_shares: (form.ownership_shares || [])
        .filter((s) => s && s.contact_id && Number(s.share_percent) > 0)
        .map((s) => ({ contact_id: s.contact_id, share_percent: Number(s.share_percent) })),
      unit_type: deriveUnitType(form, existing),
      ...commercialPayload(form),
      // 2026-07 — Adress-Variante (primary/secondary)
      address_variant: form.address_variant === "secondary" ? "secondary" : "primary",
      // iter162 — Pro-Einheit-Cutoffs (WEG/SEG). Leer = erbt vom Objekt.
      owned_since: form.owned_since || null,
      management_start: form.management_start || null,
    };
    try {
      if (isEdit) {
        await api.patch(`/properties/units/${existing.id}`, payload);
        toast.success("Einheit aktualisiert");
      } else {
        await api.post(`/properties/${propertyId}/units`, payload);
        toast.success("Einheit angelegt");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };
  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{isEdit ? `Einheit bearbeiten – ${existing.unit_number}` : "Neue Einheit"}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? "Änderungen werden sofort gespeichert. Die Einheits-ID wird ggf. neu berechnet (Typ/Geschoss/Position)."
            : "Die Einheits-ID wird automatisch aus Objekt-Nr., Typ, Geschoss und Position erzeugt."}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <SectionLocal title="Identifikation">
          <div className="grid grid-cols-3 gap-3">
            <F label="Einheitstyp">
              <Select value={String(form.unit_kind)} onValueChange={(v) => set("unit_kind", Number(v))}>
                <SelectTrigger data-testid="unit-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(UNIT_KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Geschoss">
              <Select value={form.floor_code} onValueChange={(v) => set("floor_code", v)}>
                <SelectTrigger data-testid="unit-floor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FLOOR_CODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Position">
              <Select value={String(form.position)} onValueChange={(v) => { set("position", Number(v)); if (!form.side && POSITIONS[v] && Number(v) > 0) set("side", POSITIONS[v]); }}>
                <SelectTrigger data-testid="unit-position"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(POSITIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Lage im Geschoss">
              <Select value={form.side || "none"} onValueChange={(v) => set("side", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="unit-side"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— keine Angabe —</SelectItem>
                  {UNIT_SIDES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Lage / Bezeichnung" className="col-span-2"><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="z. B. EG links" data-testid="unit-location" /></F>
            {Number(form.unit_kind) === 2 && <CommercialTypeSelect form={form} set={set} />}
            {/* Sprint 5c — Einheits-Nummer als Freitext nachträglich editierbar */}
            {isEdit && (
              <F label="Einheits-Nummer (manuell)" className="col-span-3">
                <Input
                  value={form.unit_number_override || ""}
                  onChange={(e) => set("unit_number_override", e.target.value)}
                  placeholder={existing.unit_number}
                  className="font-mono"
                  data-testid="unit-number-override"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Leer lassen, um sie automatisch aus Typ + Geschoss + Position zu berechnen.
                  Eigene Eingabe überschreibt das Schema (z. B. „M-01-PH").
                </div>
              </F>
            )}
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(UNIT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>

          {/* 2026-07 — Adress-Variante bei Objekten mit 2 Anschriften */}
          {hasSecondaryAddress && (
            <div className="mt-3 bg-emerald-50/50 border border-emerald-200 rounded-md p-3" data-testid="unit-address-variant-block">
              <F label="Anschrift dieser Einheit">
                <Select
                  value={form.address_variant || "primary"}
                  onValueChange={(v) => set("address_variant", v)}
                >
                  <SelectTrigger data-testid="unit-address-variant"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Hauptanschrift des Objekts</SelectItem>
                    <SelectItem value="secondary">{secondaryLabel}</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-500 mt-1">
                  Wird bei Mietverhältnissen automatisch als Anschrift des Mieters übernommen.
                </div>
              </F>
            </div>
          )}

          {/* Phase 21α: Mehrere Geschosse + Wohnungstyp */}
          {Number(form.unit_kind) === 1 && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-3" data-testid="apartment-extra-fields">
              <F label="Wohnungstyp">
                <Select
                  value={form.apartment_subtype || "none"}
                  onValueChange={(v) => set("apartment_subtype", v === "none" ? "" : v)}
                >
                  <SelectTrigger data-testid="apartment-subtype-select"><SelectValue placeholder="— optional —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— nicht angegeben —</SelectItem>
                    {Object.entries(APARTMENT_SUBTYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-500 mt-1">
                  Optionale Detailangabe für Exposés und interne Klassifizierung.
                </div>
              </F>
              <div>
                <Label className="text-xs text-slate-700">Weitere Geschosse (Maisonette, Mehrebenen)</Label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-1 mt-1 bg-white border border-slate-200 rounded p-2">
                  {Object.entries(FLOOR_CODES).map(([code, label]) => {
                    const isMain = code === form.floor_code;
                    const isSelected = (form.additional_floor_codes || []).includes(code);
                    return (
                      <label
                        key={code}
                        className={`flex items-center gap-1.5 text-[11px] px-1.5 py-1 rounded cursor-pointer transition ${
                          isMain ? "bg-slate-100 text-slate-400 cursor-not-allowed" :
                          isSelected ? "bg-emerald-50 border border-emerald-200 text-emerald-800" :
                          "hover:bg-slate-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={isMain}
                          checked={isMain || isSelected}
                          onChange={() => !isMain && toggleAdditionalFloor(code)}
                          data-testid={`additional-floor-${code}`}
                        />
                        <span className="truncate">{label}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Hauptgeschoss ist bereits markiert. Wähle hier zusätzliche Etagen aus,
                  wenn die Einheit sich über mehrere Geschosse erstreckt (z. B. Maisonette).
                </div>
              </div>
            </div>
          )}
          <div className="bg-stone-50 border border-stone-200 rounded-sm p-2.5 text-xs mt-3 text-slate-600">
            {isEdit
              ? <>Bei Änderung an Typ, Geschoss oder Position wird die Einheits-ID neu berechnet (Objekt-Nr.{propertyNumber ? <span className="font-mono"> · {propertyNumber}</span> : null}).</>
              : <>Die Wohnungs-ID wird automatisch erzeugt, sobald die Einheit gespeichert wird.</>}
          </div>
        </SectionLocal>

        {/* WEG Phase 1 — Miteigentumsanteile, Sondereigentum, Eigentümer
            + 2026-02 (iter116) MEA in Zähler/Nenner/‰/% + SEG-ETW-Struktur */}
        {(isWEG || objectType === "seg") && (
          <SectionLocal title="WEG-Angaben (Miteigentumsanteile)">
            {(() => {
              const shareNum = Number(form.weg_share) || 0;
              const denom = Number(form.weg_share_denominator) || Number(property?.weg_total_shares) || 1000;
              const permille = denom > 0 ? (shareNum * 1000 / denom) : 0;
              const percent = denom > 0 ? (shareNum * 100 / denom) : 0;
              return (
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-2">
                    <F label="MEA-Zähler">
                      <Input
                        type="number" min="0" step="1" value={form.weg_share}
                        onChange={(e) => set("weg_share", e.target.value)}
                        placeholder="250"
                        data-testid="unit-weg-share"
                      />
                    </F>
                  </div>
                  <div className="col-span-2">
                    <F label={`Nenner (Objekt: ${property?.weg_total_shares ?? 1000})`}>
                      <Input
                        type="number" min="1" step="1" value={form.weg_share_denominator}
                        onChange={(e) => set("weg_share_denominator", e.target.value)}
                        placeholder={String(property?.weg_total_shares ?? 1000)}
                        data-testid="unit-weg-share-denominator"
                      />
                    </F>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Nur füllen wenn abweichend (z. B. SEG-ETW mit eigener WEG-Basis).
                    </div>
                  </div>
                  <div className="col-span-2 flex items-end">
                    <div
                      className="w-full bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-[11px] leading-tight"
                      data-testid="unit-mea-preview"
                    >
                      <div className="text-slate-500 uppercase tracking-wider font-semibold text-[9px]">Berechnete Basis</div>
                      <div className="font-mono text-slate-800">{shareNum}/{denom}</div>
                      <div className="text-slate-600">= {permille.toFixed(3)} ‰ · {percent.toFixed(3)} %</div>
                    </div>
                  </div>
                  <div className="col-span-3">
                    <F label="Zuordnung">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer pt-1">
                        <input
                          type="checkbox"
                          checked={form.is_sondereigentum !== false}
                          onChange={(e) => set("is_sondereigentum", e.target.checked)}
                          data-testid="unit-is-sondereigentum"
                        />
                        Sondereigentum
                      </label>
                    </F>
                  </div>
                  <div className="col-span-3">
                    <F label="Eigentümer">
                      <SearchableCombobox
                        value={form.owner_contact_id || ""}
                        onChange={(v) => set("owner_contact_id", v)}
                        options={ownerCandidates.filter((c) => c && c.id).map((c) => ({
                          id: c.id,
                          label: `${c.owner_number ? `[${c.owner_number}] ` : ""}${c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company || c.company_name || c.id}`,
                          sublabel: [c.emails?.[0], c.phones?.[0]].filter(Boolean).join(" · ") || undefined,
                          keywords: [c.owner_number, c.first_name, c.last_name, c.company, c.company_name, ...(c.emails || []), ...(c.phones || [])].filter(Boolean).join(" "),
                        }))}
                        placeholder="Eigentümer suchen…"
                        allowClear
                        testId="unit-owner-select"
                      />
                    </F>
                  </div>
                </div>
              );
            })()}

            {/* 2026-09 — Miteigentümer & Quoten (WEG/Mixed/SEG) */}
            {(objectType === "weg" || objectType === "mixed" || objectType === "seg") && (
              <UnitCoOwnersEditor
                form={form}
                setForm={setForm}
                ownerCandidates={ownerCandidates}
              />
            )}

            {/* iter162 — Eigentum & Verwaltung pro Einheit (WEG/SEG) */}
            {(objectType === "weg" || objectType === "mixed" || objectType === "seg") && (
              <div className="mt-4 border-t border-slate-200 pt-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Eigentum & Verwaltung
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Eigentum seit">
                    <Input
                      type="date"
                      value={form.owned_since || ""}
                      onChange={(e) => set("owned_since", e.target.value)}
                      data-testid="unit-owned-since"
                    />
                    {!form.owned_since && property?.owned_since ? (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Erbt: {new Date(property.owned_since).toLocaleDateString("de-DE")}
                      </div>
                    ) : null}
                  </F>
                  <F label="Verwaltungsbeginn">
                    <Input
                      type="date"
                      value={form.management_start || ""}
                      onChange={(e) => set("management_start", e.target.value)}
                      data-testid="unit-management-start"
                    />
                    {!form.management_start && property?.management_start ? (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Erbt: {new Date(property.management_start).toLocaleDateString("de-DE")}
                      </div>
                    ) : null}
                  </F>
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                  Erbt vom Objekt, wenn leer. Sollstellungen für diese Einheit werden ab
                  dem späteren der beiden Daten geführt (Effektiver Cutoff = später von
                  Eigentum seit und Verwaltungsbeginn).
                </div>
              </div>
            )}

            {/* 2026-02 (iter116) — SEG-ETW: Einheit gehört zusätzlich zu einer separaten WEG */}
            {objectType === "seg" && (
              <div className="mt-3 border border-amber-200 bg-amber-50/40 rounded-md p-2.5">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.is_etw_in_seg}
                    onChange={(e) => set("is_etw_in_seg", e.target.checked)}
                    className="mt-0.5"
                    data-testid="unit-is-etw-in-seg"
                  />
                  <div className="text-xs flex-1">
                    <div className="font-semibold text-amber-900">Diese Einheit ist eigenständige Eigentumswohnung (Teil einer separaten WEG)</div>
                    <div className="text-[11px] text-amber-800 mt-0.5">
                      Damit weiß das System, dass für diese Einheit zusätzlich eine übergeordnete WEG existiert
                      (z. B. für Hausgeld / Wirtschaftsplan). Verknüpfe optional das WEG-Objekt.
                    </div>
                  </div>
                </label>
                {form.is_etw_in_seg && (
                  <div className="mt-2 pl-6 space-y-2">
                    <F label="Übergeordnete WEG (internes Objekt)">
                      <Select
                        value={form.parent_weg_property_id || "__none__"}
                        onValueChange={(v) => {
                          const val = v === "__none__" ? "" : v;
                          set("parent_weg_property_id", val);
                          if (val) set("parent_weg_contact_id", "");
                        }}
                      >
                        <SelectTrigger data-testid="unit-parent-weg-property"><SelectValue placeholder="WEG-Objekt wählen …" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— (keins wählen) —</SelectItem>
                          {(wegPropertyCandidates || []).filter((wp) => wp && wp.id).map((wp) => (
                            <SelectItem key={wp.id} value={wp.id}>
                              {wp.property_number || ""} · {wp.name || wp.id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </F>
                    <F label="ODER: Fremd-verwaltete WEG (Kontakt)">
                      <div className="flex gap-2">
                        <Select
                          value={form.parent_weg_contact_id || "__none__"}
                          onValueChange={(v) => {
                            const val = v === "__none__" ? "" : v;
                            set("parent_weg_contact_id", val);
                            if (val) set("parent_weg_property_id", "");
                          }}
                        >
                          <SelectTrigger className="flex-1" data-testid="unit-parent-weg-contact"><SelectValue placeholder="Fremd-verwaltete WEG wählen …" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— (später verknüpfen) —</SelectItem>
                            {(externalWegCandidates || []).filter((c) => c && c.id).map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.id}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Link
                          to="/kontakte?role=external_weg&new=1"
                          target="_blank"
                          className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[11px] text-slate-700 hover:bg-slate-50"
                          data-testid="unit-new-external-weg-link"
                          title="Fremd-verwaltete WEG als Kontakt anlegen"
                        >
                          <Plus size={12} /> Neu
                        </Link>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        Nutze diese Option, wenn die übergeordnete WEG von einer anderen Hausverwaltung geführt wird und kein eigenes Objekt in Domivus existiert.
                      </div>
                    </F>
                  </div>
                )}
              </div>
            )}
          </SectionLocal>
        )}

        {/* VM-14d — Dynamische Sektionen je Einheitstyp */}
        {[1, 2].includes(Number(form.unit_kind)) && (
          <>
            <SectionLocal title={Number(form.unit_kind) === 2 ? "Flächen & Räume (Gewerbe)" : "Flächen & Räume"}>
              {Number(form.unit_kind) === 2 ? (
              <div className="grid grid-cols-4 gap-3">
                <F label={`${unitLabels(form.unit_type, 2).area} (m²)`}><Input type="number" step="0.1" value={form.usable_area} onChange={(e) => set("usable_area", e.target.value)} data-testid="unit-usable-area" /></F>
                <F label="Räume"><Input type="number" step="0.5" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} data-testid="unit-rooms" /></F>
                <F label="Sanitärräume / WCs"><Input type="number" value={form.toilets} onChange={(e) => set("toilets", e.target.value)} data-testid="unit-toilets" /></F>
                <F label="Stellplätze"><Input type="number" value={form.parking_spots} onChange={(e) => set("parking_spots", e.target.value)} /></F>
                <F label="MEA (1000-stel)"><Input type="number" value={form.mea_share} onChange={(e) => set("mea_share", e.target.value)} placeholder="156" /></F>
                <div className="col-span-4"><CommercialFields form={form} set={set} /></div>
              </div>
              ) : (
              <div className="grid grid-cols-4 gap-3">
                <F label="Wohnfl. (m²)"><Input type="number" step="0.1" value={form.living_area} onChange={(e) => set("living_area", e.target.value)} data-testid="unit-living-area" /></F>
                <F label="Nutzfl. (m²)"><Input type="number" step="0.1" value={form.usable_area} onChange={(e) => set("usable_area", e.target.value)} /></F>
                <F label="Zimmer"><Input type="number" step="0.5" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} data-testid="unit-rooms" /></F>
                <F label="Schlafzimmer"><Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} /></F>
                <F label="Bäder"><Input type="number" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} data-testid="unit-bathrooms" /></F>
                <F label="Sep. WCs"><Input type="number" value={form.toilets} onChange={(e) => set("toilets", e.target.value)} data-testid="unit-toilets" /></F>
                <F label="MEA (1000-stel)"><Input type="number" value={form.mea_share} onChange={(e) => set("mea_share", e.target.value)} placeholder="156" /></F>
                <F label="Stellplätze"><Input type="number" value={form.parking_spots} onChange={(e) => set("parking_spots", e.target.value)} /></F>
              </div>
              )}
            </SectionLocal>

            {Number(form.unit_kind) === 1 && <SectionLocal title="Außenflächen">
              <div className="grid grid-cols-4 gap-3">
                <F label="Balkon m²"><Input type="number" step="0.1" value={form.balcony_area} onChange={(e) => { set("balcony_area", e.target.value); if (e.target.value) set("has_balcony", true); }} data-testid="unit-balcony-area" /></F>
                <F label="Loggia m²"><Input type="number" step="0.1" value={form.loggia_area} onChange={(e) => set("loggia_area", e.target.value)} data-testid="unit-loggia-area" /></F>
                <F label="Terrasse m²"><Input type="number" step="0.1" value={form.terrace_area} onChange={(e) => { set("terrace_area", e.target.value); if (e.target.value) set("has_terrace", true); }} data-testid="unit-terrace-area" /></F>
                <F label="Garten m²"><Input type="number" step="0.1" value={form.garden_area} onChange={(e) => { set("garden_area", e.target.value); if (e.target.value) set("has_garden", true); }} data-testid="unit-garden-area" /></F>
              </div>
            </SectionLocal>}

            <SectionLocal title="Keller / Speicher / Aufzug">
              <div className="grid grid-cols-4 gap-3">
                <F label="Keller m²"><Input type="number" step="0.1" value={form.cellar_area} onChange={(e) => { set("cellar_area", e.target.value); if (e.target.value) set("has_basement_room", true); }} data-testid="unit-cellar-area" /></F>
                <F label="Keller-Nr."><Input value={form.cellar_number} onChange={(e) => set("cellar_number", e.target.value)} placeholder="z. B. K-3" data-testid="unit-cellar-number" /></F>
                <F label="Speicher">
                  <div className="flex items-center h-9 gap-2">
                    <ToggleLocal label="vorhanden" checked={form.has_attic_room} onChange={(v) => set("has_attic_room", v)} />
                  </div>
                </F>
                <F label="Aufzug">
                  <Select value={form.has_elevator === null || form.has_elevator === undefined ? "none" : form.has_elevator ? "yes" : "no"} onValueChange={(v) => set("has_elevator", v === "none" ? null : v === "yes")}>
                    <SelectTrigger data-testid="unit-elevator"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— keine Angabe —</SelectItem>
                      <SelectItem value="yes">Ja</SelectItem>
                      <SelectItem value="no">Nein</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
              </div>
            </SectionLocal>

            <SectionLocal title="Ausstattung">
              <div className="grid grid-cols-3 gap-3">
                <F label="Möblierung">
                  <Select value={form.furnishing || "none"} onValueChange={(v) => set("furnishing", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="unit-furnishing"><SelectValue placeholder="— nicht angegeben —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— nicht angegeben —</SelectItem>
                      {Object.entries(FURNISHING_OPTIONS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Ausstattungsstandard">
                  <Select value={form.quality_standard || "none"} onValueChange={(v) => set("quality_standard", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="unit-quality"><SelectValue placeholder="— nicht angegeben —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— nicht angegeben —</SelectItem>
                      {Object.entries(QUALITY_STANDARD_OPTIONS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Einbauküche">
                  <div className="flex items-center h-9 gap-2">
                    <ToggleLocal label="vorhanden" checked={form.has_kitchen} onChange={(v) => set("has_kitchen", v)} />
                  </div>
                </F>
              </div>
            </SectionLocal>
          </>
        )}

        {/* Garage / Stellplatz / Tiefgarage — dynamische Garagen-Sektion */}
        {[5, 6, 7].includes(Number(form.unit_kind)) && (
          <SectionLocal title="Garage / Stellplatz">
            <div className="grid grid-cols-3 gap-3">
              <F label="Typ">
                <Select value={form.garage_kind || "none"} onValueChange={(v) => set("garage_kind", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="unit-garage-kind"><SelectValue placeholder="— wählen —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— nicht angegeben —</SelectItem>
                    {Object.entries(GARAGE_KIND_OPTIONS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Kennzeichen / Nr."><Input value={form.garage_plate} onChange={(e) => set("garage_plate", e.target.value)} placeholder="z. B. TG-42" data-testid="unit-garage-plate" /></F>
              <F label="Nutzfläche m²"><Input type="number" step="0.1" value={form.usable_area} onChange={(e) => set("usable_area", e.target.value)} /></F>
              <F label="Länge m"><Input type="number" step="0.01" value={form.garage_length} onChange={(e) => set("garage_length", e.target.value)} data-testid="unit-garage-length" /></F>
              <F label="Breite m"><Input type="number" step="0.01" value={form.garage_width} onChange={(e) => set("garage_width", e.target.value)} data-testid="unit-garage-width" /></F>
              <F label="Höhe m"><Input type="number" step="0.01" value={form.garage_height} onChange={(e) => set("garage_height", e.target.value)} data-testid="unit-garage-height" /></F>
              <F label="Torart">
                <Select value={form.garage_door_type || "none"} onValueChange={(v) => set("garage_door_type", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="unit-garage-door"><SelectValue placeholder="— wählen —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— nicht angegeben —</SelectItem>
                    {Object.entries(GARAGE_DOOR_OPTIONS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Stromanschluss">
                <Select value={form.has_power === null || form.has_power === undefined ? "none" : form.has_power ? "yes" : "no"} onValueChange={(v) => set("has_power", v === "none" ? null : v === "yes")}>
                  <SelectTrigger data-testid="unit-garage-power"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— keine Angabe —</SelectItem>
                    <SelectItem value="yes">Ja</SelectItem>
                    <SelectItem value="no">Nein</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              <F label="Wallbox">
                <Select value={form.has_wallbox === null || form.has_wallbox === undefined ? "none" : form.has_wallbox ? "yes" : "no"} onValueChange={(v) => set("has_wallbox", v === "none" ? null : v === "yes")}>
                  <SelectTrigger data-testid="unit-garage-wallbox"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— keine Angabe —</SelectItem>
                    <SelectItem value="yes">Ja</SelectItem>
                    <SelectItem value="no">Nein</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              {form.has_wallbox && (
                <F label="Wallbox kW"><Input type="number" step="0.1" value={form.wallbox_kw} onChange={(e) => set("wallbox_kw", e.target.value)} placeholder="11" data-testid="unit-garage-wallbox-kw" /></F>
              )}
            </div>
          </SectionLocal>
        )}

        {/* Keller-Einheit (unit_kind=3) */}
        {Number(form.unit_kind) === 3 && (
          <SectionLocal title="Keller">
            <div className="grid grid-cols-3 gap-3">
              <F label="Fläche m²"><Input type="number" step="0.1" value={form.cellar_area || form.usable_area} onChange={(e) => set("cellar_area", e.target.value)} data-testid="unit-cellar-area" /></F>
              <F label="Kellernummer"><Input value={form.cellar_number} onChange={(e) => set("cellar_number", e.target.value)} placeholder="z. B. K-3" data-testid="unit-cellar-number" /></F>
              <F label="MEA (1000-stel)"><Input type="number" value={form.mea_share} onChange={(e) => set("mea_share", e.target.value)} placeholder="12" /></F>
            </div>
          </SectionLocal>
        )}

        {/* Speicher-Einheit (unit_kind=4) */}
        {Number(form.unit_kind) === 4 && (
          <SectionLocal title="Speicher / Dachboden">
            <div className="grid grid-cols-3 gap-3">
              <F label="Fläche m²"><Input type="number" step="0.1" value={form.usable_area} onChange={(e) => set("usable_area", e.target.value)} /></F>
              <F label="Zugang / Notiz" className="col-span-2"><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="z. B. über Treppenhaus 2" /></F>
            </div>
          </SectionLocal>
        )}

        <SectionLocal title="Richtwert-Miete (nur Vorschlag — die echte Miete entsteht im Mietverhältnis)">
          <div className="grid grid-cols-3 gap-3">
            <F label="Kaltmiete € (Richtwert)"><Input type="number" step="0.01" value={form.default_rent_cold} onChange={(e) => set("default_rent_cold", e.target.value)} data-testid="unit-default-rent" placeholder="z. B. 850" /></F>
            <F label="NK-VZ € (Richtwert)"><Input type="number" step="0.01" value={form.default_utilities_advance} onChange={(e) => set("default_utilities_advance", e.target.value)} /></F>
            <F label="HK-VZ € (Richtwert)"><Input type="number" step="0.01" value={form.default_heating_advance} onChange={(e) => set("default_heating_advance", e.target.value)} /></F>
          </div>
        </SectionLocal>

        {isEdit && existing?.id && (
          <SectionLocal title="Protokolle">
            <ProtocolsPanel unitId={existing.id} propertyId={propertyId} />
          </SectionLocal>
        )}

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="unit-save-btn">
            {isEdit ? "Änderungen speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function unitFormFrom(u) {
  return {
    ...commercialFormFrom(u),
    unit_kind: u.unit_kind ?? 1,
    floor_code: u.floor_code ?? "00",
    additional_floor_codes: u.additional_floor_codes || [],
    apartment_subtype: u.apartment_subtype || "",
    position: u.position ?? 1,
    side: u.side ?? "",
    location: u.location ?? "",
    // 2026-07 — Adress-Variante der Einheit
    address_variant: u.address_variant === "secondary" ? "secondary" : "primary",
    living_area: u.living_area ?? "",
    usable_area: u.usable_area ?? "",
    rooms: u.rooms ?? "",
    bedrooms: u.bedrooms ?? "",
    bathrooms: u.bathrooms ?? "",
    has_separate_wc: !!u.has_separate_wc,
    has_kitchen: !!u.has_kitchen,
    has_balcony: !!u.has_balcony,
    balcony_area: u.balcony_area ?? "",
    has_terrace: !!u.has_terrace,
    has_garden: !!u.has_garden,
    has_basement_room: !!u.has_basement_room,
    has_attic_room: !!u.has_attic_room,
    parking_spots: u.parking_spots ?? "",
    parking_type: u.parking_type ?? "",
    floor_covering: u.floor_covering ?? "",
    heating_type: u.heating_type ?? "",
    hot_water_type: u.hot_water_type ?? "",
    last_renovation_year: u.last_renovation_year ?? "",
    default_rent_cold: u.default_rent_cold ?? "",
    default_utilities_advance: u.default_utilities_advance ?? "",
    default_heating_advance: u.default_heating_advance ?? "",
    mea_share: u.mea_share ?? "",
    status: u.status ?? "vacant",
    notes: u.notes ?? "",
    // WEG Phase 1
    weg_share: u.weg_share ?? "",
    weg_share_denominator: u.weg_share_denominator ?? "",
    is_etw_in_seg: !!u.is_etw_in_seg,
    parent_weg_property_id: u.parent_weg_property_id || "",
    parent_weg_contact_id: u.parent_weg_contact_id || "",
    is_sondereigentum: u.is_sondereigentum !== false,
    owner_contact_id: u.owner_contact_id || "",
    // 2026-09 — Miteigentümer/Quoten
    ownership_shares: Array.isArray(u.ownership_shares) ? u.ownership_shares.map((s) => ({ ...s })) : [],
    // iter162 — Pro-Einheit-Cutoffs
    owned_since: u.owned_since || "",
    management_start: u.management_start || "",
    // VM-14d — Erweiterte Ausstattungs-Felder
    toilets: u.toilets ?? "",
    loggia_area: u.loggia_area ?? "",
    terrace_area: u.terrace_area ?? "",
    garden_area: u.garden_area ?? "",
    cellar_area: u.cellar_area ?? "",
    cellar_number: u.cellar_number ?? "",
    has_elevator: u.has_elevator == null ? null : !!u.has_elevator,
    furnishing: u.furnishing ?? "",
    quality_standard: u.quality_standard ?? "",
    // Garage-spezifisch
    garage_kind: u.garage_kind ?? "",
    garage_plate: u.garage_plate ?? "",
    garage_length: u.garage_length ?? "",
    garage_width: u.garage_width ?? "",
    garage_height: u.garage_height ?? "",
    garage_door_type: u.garage_door_type ?? "",
    has_power: u.has_power == null ? null : !!u.has_power,
    has_wallbox: u.has_wallbox == null ? null : !!u.has_wallbox,
    wallbox_kw: u.wallbox_kw ?? "",
  };
}

// ============================================================
// Hausmeister Tab (Multi-Caretaker via Contacts)
// ============================================================
function CaretakersTab({ property: p, onChange }) {
  const [contacts, setContacts] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [keyDialog, setKeyDialog] = useState(null); // { caretakerId, contact }

  const loadContacts = async () => {
    const ids = p.caretaker_contact_ids || [];
    if (ids.length === 0) { setContacts([]); return; }
    const results = await Promise.all(
      ids.map((id) => api.get(`/contacts/${id}`).then((r) => r.data).catch(() => null))
    );
    setContacts(results.filter(Boolean));
  };

  const loadCandidates = async () => {
    const { data } = await api.get("/contacts", { params: { role: "caretaker" } });
    setAllCandidates(data);
  };

  useEffect(() => { loadContacts(); }, [p.caretaker_contact_ids]);
  useEffect(() => { loadCandidates(); }, []);

  const addCaretaker = async (contactId) => {
    if (!contactId) return;
    const newIds = Array.from(new Set([...(p.caretaker_contact_ids || []), contactId]));
    try {
      await api.patch(`/properties/${p.id}`, { ...p, caretaker_contact_ids: newIds });
      toast.success("Hausmeister hinzugefügt");
      setAddOpen(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const removeCaretaker = async (contactId) => {
    if (!confirm("Hausmeister entfernen?")) return;
    const newIds = (p.caretaker_contact_ids || []).filter((x) => x !== contactId);
    try {
      await api.patch(`/properties/${p.id}`, { ...p, caretaker_contact_ids: newIds });
      toast.success("Entfernt");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const available = allCandidates.filter((c) => !(p.caretaker_contact_ids || []).includes(c.id));

  return (
    <SectionCard
      title={`Hausmeister (${contacts.length})`}
      action={
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1.5 h-8" data-testid="add-caretaker-btn">
              <Plus size={14} /> Hausmeister hinzufügen
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Hausmeister aus Kontakten wählen</DialogTitle></DialogHeader>
            {available.length === 0 ? (
              <div className="text-sm text-slate-500">
                Keine weiteren Kontakte mit Rolle „Hausmeister" verfügbar. Bitte einen Kontakt unter „Kontakte" anlegen und die Rolle „caretaker" zuweisen.
              </div>
            ) : (
              <ul className="divide-y divide-stone-100 max-h-80 overflow-y-auto border border-stone-200 rounded-md">
                {available.map((c) => (
                  <li key={c.id}>
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center justify-between"
                      onClick={() => addCaretaker(c.id)}
                      data-testid={`caretaker-pick-${c.id}`}
                    >
                      <div>
                        <div className="text-sm font-medium text-slate-900">{c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "—"}</div>
                        <div className="text-xs text-slate-500">{firstEmail(c.emails)}</div>
                      </div>
                      <ArrowRight size={14} className="text-slate-400" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </DialogContent>
        </Dialog>
      }
    >
      {contacts.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-8">Noch kein Hausmeister zugeordnet.</div>
      ) : (
        <ul className="space-y-2">
          {contacts.map((c) => {
            const display = c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "Hausmeister";
            const keys = (p.keys || []).filter((k) => k.holder_contact_id === c.id);
            return (
              <li key={c.id} className="bg-white border border-stone-200 rounded-md p-3 shadow-sm" data-testid={`caretaker-row-${c.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <Link to={`/kontakte/${c.id}`} className="font-medium text-slate-900 hover:text-slate-700">
                      {display}
                    </Link>
                    <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                      {(c.emails || []).slice(0, 1).map((e, idx) => {
                        const text = displayEmail(e);
                        return text ? (
                          <div key={idx} className="flex items-center gap-1.5"><EnvelopeSimple size={12} /> {text}</div>
                        ) : null;
                      })}
                      {(c.phones || []).map((p) => (typeof p === "string" ? p : (p?.number || p?.phone || ""))).filter(Boolean).slice(0, 2).map((ph) => (
                        <div key={ph} className="flex items-center gap-1.5"><Phone size={12} /> {ph}</div>
                      ))}
                    </div>
                    {keys.length > 0 && (
                      <div className="mt-2 text-xs text-slate-700">
                        <span className="font-medium">Schlüssel:</span>{" "}
                        {keys.map((k) => `${k.label}${k.count > 1 ? ` (${k.count})` : ""}`).join(", ")}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-400 italic mt-1">
                      Anwesenheitsplan folgt mit dem Kalender-Modul.
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-xs"
                      onClick={() => setKeyDialog({ caretakerId: c.id, contact: c })}
                      data-testid={`caretaker-add-key-${c.id}`}>
                      Schlüssel zuweisen
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => removeCaretaker(c.id)}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {keyDialog && (
        <Dialog open={!!keyDialog} onOpenChange={(v) => !v && setKeyDialog(null)}>
          <KeyDialog
            propertyId={p.id}
            units={p.units || []}
            defaultHolder={{ type: "caretaker", contactId: keyDialog.caretakerId, label: "Hausmeisterschlüssel" }}
            onSaved={() => { setKeyDialog(null); onChange(); }}
          />
        </Dialog>
      )}
    </SectionCard>
  );
}

// ============================================================
// Schlüssel Tab
// ============================================================
function KeysTab({ property: p, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [unitFilter, setUnitFilter] = useState("all");

  const keys = (p.keys || []).filter((k) => unitFilter === "all" ? true : unitFilter === "none" ? !k.unit_id : k.unit_id === unitFilter);

  const remove = async (k) => {
    if (!confirm(`Schlüssel "${k.label}" wirklich löschen?`)) return;
    try {
      await api.delete(`/properties/${p.id}/keys/${k.id}`);
      toast.success("Schlüssel gelöscht");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const unitById = useMemo(() => {
    const m = {};
    (p.units || []).forEach((u) => { m[u.id] = u; });
    return m;
  }, [p.units]);

  return (
    <SectionCard
      title={`Schlüssel (${(p.keys || []).length})`}
      action={
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setEditing(null)} data-testid="add-key-btn">
              <Plus size={14} /> Schlüssel hinzufügen
            </Button>
          </DialogTrigger>
          <KeyDialog
            propertyId={p.id}
            units={p.units || []}
            existing={editing}
            onSaved={() => { setOpen(false); setEditing(null); onChange(); }}
          />
        </Dialog>
      }
    >
      {(p.units || []).length > 0 && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <Label className="text-xs text-slate-600">Filter Einheit:</Label>
          <Select value={unitFilter} onValueChange={setUnitFilter}>
            <SelectTrigger className="w-64 h-8" data-testid="keys-filter-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="none">Nur Objekt-Schlüssel (keine Einheit)</SelectItem>
              {(p.units || []).map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.unit_number} · {u.location || ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {keys.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-8">Keine Schlüssel erfasst.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-slate-700 text-left">
            <tr>
              <Th>Bezeichnung</Th><Th>Nr.</Th><Th className="text-right">Anzahl</Th>
              <Th>Inhaber</Th><Th>Einheit</Th><Th>Übergeben</Th><Th className="w-20">Aktion</Th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-t border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => { setEditing(k); setOpen(true); }} data-testid={`key-row-${k.id}`}>
                <Td className="font-medium">{k.label}</Td>
                <Td className="font-mono">{k.key_number || "—"}</Td>
                <Td className="text-right tabular-nums">{k.count}</Td>
                <Td className="text-xs">
                  <KeyHolderLabel propertyKey={k} />
                </Td>
                <Td>{k.unit_id ? (unitById[k.unit_id]?.unit_number || "—") : <span className="text-slate-400">Objekt</span>}</Td>
                <Td>{formatDate(k.given_at)}</Td>
                <Td onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(k); setOpen(true); }}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => remove(k)}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionCard>
  );
}

function KeyHolderLabel({ propertyKey: k }) {
  const [label, setLabel] = useState("");
  useEffect(() => {
    let active = true;
    (async () => {
      if (k.holder_contact_id) {
        try {
          const { data } = await api.get(`/contacts/${k.holder_contact_id}`);
          if (active) setLabel(data.display_name || `${data.first_name || ""} ${data.last_name || ""}`.trim() || data.company_name || "Kontakt");
        } catch { /* ignore */ }
      } else if (k.holder_user_id) {
        try {
          const { data } = await api.get("/users/staff-list");
          if (active) setLabel(data.find((u) => u.id === k.holder_user_id)?.name || "Benutzer");
        } catch { /* ignore */ }
      } else {
        if (active) setLabel("—");
      }
    })();
    return () => { active = false; };
  }, [k.holder_contact_id, k.holder_user_id]);
  return (
    <div>
      {k.holder_type && <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">{k.holder_type}</span>}
      <span className="text-slate-700">{label}</span>
    </div>
  );
}

function KeyDialog({ propertyId, units, existing, defaultHolder, onSaved }) {
  const [contacts, setContacts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(() => ({
    label: existing?.label || defaultHolder?.label || "Haustürschlüssel",
    key_number: existing?.key_number || "",
    count: existing?.count ?? 1,
    holder_type: existing?.holder_type || defaultHolder?.type || "tenant",
    holder_contact_id: existing?.holder_contact_id || defaultHolder?.contactId || "",
    holder_user_id: existing?.holder_user_id || "",
    unit_id: existing?.unit_id || "",
    given_at: existing?.given_at || "",
    returned_at: existing?.returned_at || "",
    notes: existing?.notes || "",
  }));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/contacts").then(({ data }) => setContacts(data)).catch(() => setContacts([]));
    api.get("/users/staff-list").then(({ data }) => setStaff(data)).catch(() => setStaff([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      label: form.label,
      key_number: form.key_number || null,
      count: Number(form.count) || 1,
      holder_type: form.holder_type || null,
      holder_contact_id: form.holder_contact_id || null,
      holder_user_id: form.holder_user_id || null,
      unit_id: form.unit_id || null,
      given_at: form.given_at || null,
      returned_at: form.returned_at || null,
      notes: form.notes || null,
    };
    try {
      if (existing?.id) {
        await api.patch(`/properties/${propertyId}/keys/${existing.id}`, payload);
        toast.success("Schlüssel aktualisiert");
      } else {
        await api.post(`/properties/${propertyId}/keys`, payload);
        toast.success("Schlüssel angelegt");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader><DialogTitle>{existing ? "Schlüssel bearbeiten" : "Neuer Schlüssel"}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <F label="Bezeichnung" className="col-span-2"><Input required value={form.label} onChange={(e) => set("label", e.target.value)} data-testid="key-label" /></F>
          <F label="Anzahl"><Input type="number" min={1} value={form.count} onChange={(e) => set("count", e.target.value)} /></F>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Schlüssel-Nr."><Input value={form.key_number} onChange={(e) => set("key_number", e.target.value)} /></F>
          <F label="Zugeordnete Einheit (optional)">
            <Select value={form.unit_id || "none"} onValueChange={(v) => set("unit_id", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="— Objekt-Schlüssel —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Objekt-Schlüssel —</SelectItem>
                {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.unit_number} · {u.location || ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Inhaber-Typ">
            <Select value={form.holder_type || "none"} onValueChange={(v) => set("holder_type", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="key-holder-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                <SelectItem value="tenant">Mieter</SelectItem>
                <SelectItem value="owner">Eigentümer</SelectItem>
                <SelectItem value="caretaker">Hausmeister</SelectItem>
                <SelectItem value="manager">Verwaltung/Betreuer</SelectItem>
                <SelectItem value="other">Sonstige</SelectItem>
              </SelectContent>
            </Select>
          </F>
          {form.holder_type === "manager" ? (
            <F label="Benutzer" className="col-span-2">
              <Select value={form.holder_user_id || "none"} onValueChange={(v) => set("holder_user_id", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          ) : (
            <F label="Kontakt" className="col-span-2">
              <Select value={form.holder_contact_id || "none"} onValueChange={(v) => set("holder_contact_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="key-holder-contact"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {contacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || c.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Übergeben am"><Input type="date" value={form.given_at} onChange={(e) => set("given_at", e.target.value)} /></F>
          <F label="Zurückerhalten am"><Input type="date" value={form.returned_at} onChange={(e) => set("returned_at", e.target.value)} /></F>
        </div>
        <F label="Notizen"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></F>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="key-save-btn">{existing ? "Speichern" : "Anlegen"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// ============================================================
// Dokumente Tab (Property-weit)
// ============================================================
function DocumentsTab({ property: p }) {
  return (
    <SectionCard title="Dokumente">
      <DocumentsBlock propertyId={p.id} title="Alle Dokumente zum Objekt" />
    </SectionCard>
  );
}


function SectionLocal({ title, children }) {
  return (
    <div className="border border-stone-200 rounded-md p-3 bg-stone-50/40">
      <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function ToggleLocal({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
      <input type="checkbox" className="h-4 w-4 accent-slate-900" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-slate-700">{label}</span>
    </label>
  );
}


// 2026-08 — SEG-Analysen direkt aus der Objekt-Detail-Ansicht:
// Auflistung + Direkt-Sprung in den SEG-Assistenten für dieses Objekt.
function SegAnalysesTab({ property }) {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!property?.id) return;
    setLoading(true);
    api.get(`/seg-analyses?property_id=${property.id}`)
      .then(({ data }) => setAnalyses(data || []))
      .catch(() => setAnalyses([]))
      .finally(() => setLoading(false));
  }, [property?.id]);

  return (
    <div className="space-y-4" data-testid="property-seg-analyses-tab">
      <div className="bg-purple-50 border border-purple-200 rounded-md p-3 text-sm text-purple-900">
        <b>Sondereigentumsverwaltung (SEG)</b> — Bereiten Sie die anstehende
        Eigentümerversammlung des externen WEG-Verwalters für Ihren Sondereigentümer vor.
      </div>
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {loading ? "Lade …" : `${analyses.length} Analyse(n) für dieses Objekt`}
        </div>
        <Link to={`/seg/analysen/${property.id}`}>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="property-seg-open">
            <Plus size={13} /> Analyse anlegen / öffnen
          </Button>
        </Link>
      </div>
      {analyses.length === 0 && !loading ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500 text-sm">
          Noch keine SEG-Analysen. Legen Sie eine für die nächste WEG-Versammlung an.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Datum</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">WEG-Referenz</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">TOPs</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {analyses.map((a) => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(a.assembly_date)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{a.weg_property_ref || "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{(a.agenda_items || []).length}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border">{a.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/seg/analysen/${property.id}`}>
                      <Button size="sm" variant="ghost" className="h-7 text-xs">Öffnen</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// 2026-02 — Schäden-Tab am Objekt: Liste aller Schäden dieses Objekts,
// mit „+ Neuer Schaden" (Prefill property_id) und Klick → Detail-Seite.
function PropertyDamagesTab({ property }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/damages", { params: { property_id: property.id } });
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [property.id]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Schäden</h2>
          <p className="text-xs text-slate-500">Alle Schadensmeldungen zu diesem Objekt.</p>
        </div>
        <Button size="sm" onClick={() => setOpenCreate(true)} data-testid="property-new-damage">
          <Plus size={14} className="mr-1" /> Neuer Schaden
        </Button>
      </div>

      {loading && <div className="text-sm text-slate-500">Lade…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-sm text-slate-500">
          Noch keine Schäden zu diesem Objekt erfasst.
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Schaden-Nr.</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Titel</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Status</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Datum</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/schaden/${d.id}`)}
                    data-testid={`property-damage-row-${d.id}`}>
                  <td className="px-3 py-2 font-medium tabular-nums text-slate-900">{d.damage_number}</td>
                  <td className="px-3 py-2 text-slate-800">{d.title}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{d.status}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 tabular-nums">{formatDate(d.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openCreate && (
        <CreateDamageDialogLazy
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onCreated={(d) => { load(); if (d?.id) navigate(`/schaden/${d.id}`); }}
          prefill={{ property_id: property.id }}
        />
      )}
    </div>
  );
}

// Lazy-Import um Zirkel-Imports zu vermeiden
function CreateDamageDialogLazy(props) {
  const [Comp, setComp] = useState(null);
  useEffect(() => {
    import("@/pages/Schaeden").then((m) => setComp(() => m.CreateDamageDialog));
  }, []);
  if (!Comp) return null;
  return <Comp {...props} />;
}

